import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastWarning } from "../../../utils/showApiErrorToast";
import {
  Badge as ChakraBadge,
  Alert as ChakraAlert,
  AlertDescription as ChakraAlertDescription,
  AlertIcon as ChakraAlertIcon,
  AlertTitle as ChakraAlertTitle,
  Box as ChakraBox,
  Button as ChakraButton,
  Divider as ChakraDivider,
  HStack as ChakraHStack,
  IconButton as ChakraIconBtn,
  SimpleGrid as ChakraSimpleGrid,
  Stack as ChakraStack,
  Table as ChakraTable,
  Tbody as ChakraTbody,
  Td as ChakraTd,
  Text as ChakraText,
  Th as ChakraTh,
  Thead as ChakraThead,
  Tr as ChakraTr,
  Tooltip as ChakraTooltip,
  Collapse as ChakraCollapse,
  Heading as ChakraHeading,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { isTradeAnalysisReportType } from "../../../utils/reportTypeRules";
import { FaSort } from "react-icons/fa";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TradeFilterSelect } from "@/components/trade-filter-selects";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { useTradePolicyFilterCatalogFields } from "@/hooks/useTradePolicyFilterCatalogFields";
import {
  formatMarketConditionLabel,
  isMarketConditionLegacyOnlyValue,
  marketConditionPickerValuesForSelect,
} from "../../../utils/marketConditionRegime";
import { formatDirectionBarsWindow } from "../../../utils/marketRegimeDirectionLookbacks";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { SimulatorPickActionCell } from "@/components/Reports/SimulatorPickActionCell";
import { SimulatorPickLockProvider } from "@/components/Reports/SimulatorPickLockContext";
import { CircleHelpIcon } from "../../../components/ui/circle-help";
import GradientButton from "../Profile/components/GradientButton";
import type {
  ConditionAnalysisBucketRow,
  ConditionAnalysisNode,
  ConditionAnalysisScope,
  ConditionAnalysisSegmentAggregate,
  ConditionAnalysisSituationRow,
  ConditionAnalysisSlice,
  ConditionAnalysisSummaryRow,
  ConditionAnalysisTableOmittedEntry,
  ConditionAnalysisUniformSegmentContext,
} from "../../../utils/conditionAnalysisSelectors";
import {
  buildConditionAnalysisSlices,
  cohortHeadlineFromNode,
  isPivotEnvironmentSlice,
  mergedSessionMarginalRows,
  type MarketContextCandleDirection,
} from "../../../utils/conditionAnalysisSelectors";
import {
  REPORT_PICK_LIST_CHANGED_EVENT,
  addPick,
  buildPickId,
  readPickListForReport,
  type ReportPick,
  type ReportPickSliceContext,
} from "../../../utils/reportPickListStorage";
import { pickQuickStatsFromMetrics, quickStatsMetaPatch } from "../../../utils/reportPickQuickStats";
import { getRegimeActionableFilters } from "../../../utils/marketContextNavigation";
import { enrichSituationRowsWithPolicyNodes } from "../../../utils/cohortTradesResolve";
import {
  buildMetricDefinitionRows,
  mergedMetricDefinitionMap,
  sortMetricDefinitionKeys,
} from "../../../utils/marketContextMetricDefinitions";
import { MarketContextDefinitionsModal } from "../../../components/Reports/MarketContextDefinitionsModal";
import { MarketContextBucketsSituationsTabs } from "./components/MarketContextBucketsSituationsTabs";
import { MarketContextConfiguredSliceResultTable } from "./components/MarketContextConfiguredSliceResultTable";
import { ManagementPolicyInlineStrip } from "../../../components/Reports/ManagementPolicyInlineStrip";
import { MarketContextPivotTable } from "./components/MarketContextPivotTable";
import {
  OPEN_THIS_SLICE_TOOLTIP,
  SHOW_MATCHING_SLICES_TOOLTIP,
  SLICE_CONFIGURATOR_TOOLTIP,
  WIN_RATE_COLUMN_TOOLTIP,
} from "./components/marketContextTableTooltips";
import {
  buildCohortPreviewDisplayLabel,
  getCohortPreviewSlicePathLabel,
  getDisplaySliceName,
  getSliceCategoryBadgeGradient,
  getSliceCategoryLabel,
} from "./marketContextSliceDisplay";
import {
  REPORT_DETAIL_CARD_BORDER,
  reportDetailSectionCardProps,
} from "./components/ReportsCardChrome";
import {
  REPORTS_DETAIL_BORDER_DARK,
  REPORTS_DETAIL_MUTED_DARK,
  REPORTS_DETAIL_SURFACE_DARK,
} from "./reportsDetailTheme";
import type {
  ManagementCandidate,
  ManagementSegmentCatalogEntry,
} from "../../../types/tradeManagementReport";
import type { PolicyTreeV2 } from "../../../types/tradePolicy";
import {
  buildManagementCatalogLookup,
  segmentKeyFromConfigurator,
  segmentScopeFromSegmentKey,
  type ManagementCatalogLookup,
} from "../../../utils/tradeManagementReport";

export type ManagementPreviewRequest = {
  segmentKey: string;
  segmentScope?: string;
  situation?: string;
  useShortDirection?: boolean;
};

function reportSliceContextFromSelected(selected: ConditionAnalysisSlice | null): ReportPickSliceContext | undefined {
  if (!selected || selected.kind === "all") return undefined;
  const triple_candle_window =
    selected.kind === "regime_direction_trade_direction"
      ? selected.id.startsWith("regime_dir_short_td_")
        ? ("short" as const)
        : ("long" as const)
      : selected.kind === "session_regime_direction_trade_direction"
        ? ("long" as const)
      : selected.kind === "session_regime_direction_short_trade_direction"
        ? ("short" as const)
      : undefined;
  return {
    sliceId: selected.id,
    kind: selected.kind,
    label: selected.label,
    ...(selected.market_condition != null ? { market_condition: selected.market_condition } : {}),
    ...(selected.candle_direction != null ? { candle_direction: selected.candle_direction } : {}),
    ...(selected.direction != null ? { trade_direction: selected.direction } : {}),
    ...(triple_candle_window ? { triple_candle_window } : {}),
    ...(selected.session_id != null && selected.session_id.trim() ? { session_id: selected.session_id.trim() } : {}),
  };
}

function situationPickSignature(clauses: unknown): string {
  if (!Array.isArray(clauses)) return "";
  return clauses
    .map((c) => `${String((c as { key?: string })?.key ?? "")}=${String((c as { value?: string })?.value ?? "")}`)
    .filter((x) => x !== "=")
    .sort()
    .join("|");
}

// Chakra polymorphic props can trigger “union too complex” IDE diagnostics in big TSX trees.
const Box = ChakraBox as unknown as React.FC<any>;
const Alert = ChakraAlert as unknown as React.FC<any>;
const AlertIcon = ChakraAlertIcon as unknown as React.FC<any>;
const AlertTitle = ChakraAlertTitle as unknown as React.FC<any>;
const AlertDescription = ChakraAlertDescription as unknown as React.FC<any>;
const Text = ChakraText as unknown as React.FC<any>;
const Badge = ChakraBadge as unknown as React.FC<any>;
const Button = ChakraButton as unknown as React.FC<any>;
/** Must match `IconButton as ChakraIconBtn` in the `@chakra-ui/react` import above. */
const IconButton = ChakraIconBtn as unknown as React.FC<any>;
const Divider = ChakraDivider as unknown as React.FC<any>;
const HStack = ChakraHStack as unknown as React.FC<any>;
const Stack = ChakraStack as unknown as React.FC<any>;
const SimpleGrid = ChakraSimpleGrid as unknown as React.FC<any>;
const Table = ChakraTable as unknown as React.FC<any>;
const Thead = ChakraThead as unknown as React.FC<any>;
const Tbody = ChakraTbody as unknown as React.FC<any>;
const Tr = ChakraTr as unknown as React.FC<any>;
const Tooltip = ChakraTooltip as unknown as React.FC<any>;
const Th = ChakraTh as unknown as React.FC<any>;
const Td = ChakraTd as unknown as React.FC<any>;
const Collapse = ChakraCollapse as unknown as React.FC<any>;
const Heading = ChakraHeading as unknown as React.FC<any>;

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function fmtPct(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(2)}%`;
}

function fmtNum(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(0);
  if (abs >= 100) return v.toFixed(1);
  return v.toFixed(2);
}

function fmtPnl(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function fmtInt(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return String(Math.round(v));
}

function marginalSampleByLowerKey(
  rows: ConditionAnalysisSummaryRow[] | undefined
): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows ?? []) {
    const k = (r.key ?? "").trim().toLowerCase();
    if (!k) continue;
    m.set(k, Math.floor(r.sample_size ?? 0));
  }
  return m;
}

/** Title-case single token (e.g. regime / trade side from slice context). */
function wordCapitalize(s: string): string {
  const t = s.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

function BestWorstHeader(props: { title: string; count?: number; subtleText: string; textColor: string }): React.ReactElement {
  return (
    <HStack justify="space-between" align="center">
      <Text fontSize="sm" fontWeight="semibold" color={props.textColor}>
        {props.title}
      </Text>
      <Text fontSize="xs" color={props.subtleText}>
        {props.count != null ? `${props.count}` : "—"}
      </Text>
    </HStack>
  );
}

function AnalysisScopePanel(props: {
  scope?: ConditionAnalysisScope;
  subtleText: string;
  textColor: string;
  borderColor: string;
  panelBg: string;
}): React.ReactElement | null {
  const sc = props.scope;
  if (!sc || (!sc.path && !sc.segment_key && sc.trade_count == null)) return null;
  const { isOpen, onToggle } = useDisclosure();
  return (
    <Box mb={3} borderWidth="1px" borderRadius="6px" borderColor={props.borderColor} bg={props.panelBg} p={2}>
      <Button size="xs" variant="ghost" colorScheme="teal" onClick={onToggle}>
        {isOpen ? "Hide" : "Show"} analysis cohort scope
      </Button>
      <Text fontSize="xs" color={props.subtleText} mt={1}>
        Shows whether best/worst numbers are for the full cohort or a nested segment (when the backend sends{" "}
        <Box as="span" fontFamily="mono">
          analysis_scope
        </Box>
        ).
      </Text>
      <Collapse in={isOpen} animateOpacity>
        <Stack mt={2} spacing={1}>
          {sc.path ? (
            <Text fontSize="xs" fontFamily="mono" color={props.textColor} whiteSpace="pre-wrap" wordBreak="break-word">
              path: {sc.path}
            </Text>
          ) : null}
          {sc.segment_key ? (
            <Text fontSize="xs" fontFamily="mono" color={props.textColor} whiteSpace="pre-wrap" wordBreak="break-word">
              segment_key: {sc.segment_key}
            </Text>
          ) : null}
          {sc.trade_count != null ? (
            <Text fontSize="xs" fontFamily="mono" color={props.textColor}>
              trade_count: {sc.trade_count}
            </Text>
          ) : null}
        </Stack>
      </Collapse>
    </Box>
  );
}

type SliceHeadlineOptions = {
  /**
   * Full-cohort condition-analysis node (typically the "All trades" slice). Used so `session_time` pivot rows read the
   * same `regime_summary.session` marginal as the Trading session sub-table when session slices come from
   * `regime_breakdown.by_session` nested nodes.
   */
  sessionMarginalSourceNode?: ConditionAnalysisNode | null;
};

/** Match `regime_summary` row `key` to a slice dimension (exact match, then case-insensitive). */
function findRegimeSummaryRowByKey(
  rows: ConditionAnalysisSummaryRow[] | undefined,
  key: string
): ConditionAnalysisSummaryRow | undefined {
  const id = key.trim();
  if (!id) return undefined;
  const lower = id.toLowerCase();
  let caseInsensitive: ConditionAnalysisSummaryRow | undefined;
  for (const r of rows ?? []) {
    const k = r.key?.trim();
    if (!k) continue;
    if (k === id) return r;
    if (k.toLowerCase() === lower) caseInsensitive = caseInsensitive ?? r;
  }
  return caseInsensitive;
}

/** Match `regime_summary.session[].key` to slice `session_id` (BE mixes `london` / `London`, etc.). */
function findRegimeSummarySessionRow(
  rows: ConditionAnalysisSummaryRow[] | undefined,
  sessionId: string
): ConditionAnalysisSummaryRow | undefined {
  return findRegimeSummaryRowByKey(rows, sessionId);
}

function sliceHeadline(slice: ConditionAnalysisSlice, options?: SliceHeadlineOptions): {
  sample: number | undefined;
  winRate: number | undefined;
  totalPnl: number | undefined;
  expectancy: number | undefined;
  bestSituation: string | undefined;
  worstSituation: string | undefined;
} {
  const node = slice.node;
  const bestSituation = node.market_condition_brackets?.best_situations?.[0]?.situation;
  const worstSituation = node.market_condition_brackets?.worst_situations?.[0]?.situation;
  const scopeN = node.analysis_scope?.trade_count;

  if (slice.kind === "all") {
    const nested = cohortHeadlineFromNode(node);
    return {
      sample: nested.sample ?? scopeN,
      winRate: nested.winRate,
      totalPnl: nested.totalPnl,
      expectancy: nested.expectancy,
      bestSituation,
      worstSituation,
    };
  }

  if (slice.kind === "market_direction" && slice.candle_direction) {
    const row = findRegimeSummaryRowByKey(node.regime_summary?.candle_direction, slice.candle_direction);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "market_direction_short" && slice.candle_direction) {
    const row = findRegimeSummaryRowByKey(node.regime_summary?.candle_direction_short, slice.candle_direction);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    (slice.kind === "candle_direction" || slice.kind === "candle_direction_short") &&
    slice.candle_direction
  ) {
    const table =
      slice.kind === "candle_direction_short"
        ? node.regime_summary?.candle_direction_short
        : node.regime_summary?.candle_direction;
    const row = findRegimeSummaryRowByKey(table, slice.candle_direction);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "direction" && slice.direction) {
    const row = findRegimeSummaryRowByKey(node.regime_summary?.trade_direction, slice.direction);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "session_time" && slice.session_id) {
    const nestedSessionRow = findRegimeSummarySessionRow(node.regime_summary?.session, slice.session_id);
    const rootSessionRow = findRegimeSummarySessionRow(
      options?.sessionMarginalSourceNode?.regime_summary?.session,
      slice.session_id
    );
    const sessionRowMatchesScope = (r: (typeof nestedSessionRow) | undefined) =>
      r != null &&
      typeof scopeN === "number" &&
      scopeN > 0 &&
      (r.sample_size ?? 0) === scopeN;
    // Nested `by_session.*` nodes sometimes carry a stale or sub-cohort `regime_summary.session` row
    // that does not match `analysis_scope.trade_count`. Prefer rows that match scope; favor root marginals
    // (same cohort as Session Performance / `session_analysis`) over mismatched nested rows.
    // Never fall back to a session row that disagrees with `trade_count` when scope is known — that would
    // skip the trade_direction / cohortHeadline path (e.g. London `London` vs `london` key mismatch on root).
    const row =
      typeof scopeN === "number" && scopeN > 0
        ? sessionRowMatchesScope(rootSessionRow)
          ? rootSessionRow
          : sessionRowMatchesScope(nestedSessionRow)
            ? nestedSessionRow
            : undefined
        : nestedSessionRow ?? rootSessionRow;
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    // Nested `by_session.*` nodes usually omit `regime_summary.session`; prefer marginals that span the segment.
    if (typeof scopeN === "number" && scopeN > 0) {
      const td = node.regime_summary?.trade_direction;
      if (td?.length === 1 && (td[0].sample_size ?? 0) === scopeN) {
        const r = td[0];
        return {
          sample: r.sample_size,
          winRate: r.win_rate,
          totalPnl: r.total_pnl,
          expectancy: r.expectancy,
          bestSituation,
          worstSituation,
        };
      }
      const cd = node.regime_summary?.candle_direction;
      if (cd?.length === 1 && (cd[0].sample_size ?? 0) === scopeN) {
        const r = cd[0];
        return {
          sample: r.sample_size,
          winRate: r.win_rate,
          totalPnl: r.total_pnl,
          expectancy: r.expectancy,
          bestSituation,
          worstSituation,
        };
      }
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    if (scopeN != null) {
      return {
        sample: scopeN,
        winRate: undefined,
        totalPnl: undefined,
        expectancy: undefined,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "session_trade_direction" && slice.session_id && slice.direction) {
    const key = `${slice.session_id}|${slice.direction}`;
    const row = node.regime_summary?.session_and_trade_direction?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "session_candle_direction" && slice.session_id && slice.candle_direction) {
    const key = `${slice.session_id}|${slice.candle_direction}`;
    const row = node.regime_summary?.session_and_candle_direction?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "session_candle_direction_short" && slice.session_id && slice.candle_direction) {
    const key = `${slice.session_id}|${slice.candle_direction}`;
    const row = node.regime_summary?.session_and_candle_direction_short?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (slice.kind === "session_regime" && slice.session_id && slice.market_condition) {
    const key = `${slice.session_id}|${slice.market_condition}`;
    const row = node.regime_summary?.session_and_market_condition?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    slice.kind === "session_regime_trade_direction" &&
    slice.session_id &&
    slice.market_condition &&
    slice.direction
  ) {
    const key = `${slice.session_id}|${slice.market_condition}|${slice.direction}`;
    const row = node.regime_summary?.session_and_market_condition_and_trade_direction?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    slice.kind === "session_regime_direction_trade_direction" &&
    slice.session_id &&
    slice.market_condition &&
    slice.candle_direction &&
    slice.direction
  ) {
    const key = `${slice.session_id}|${slice.market_condition}|${slice.candle_direction}|${slice.direction}`;
    const row = node.regime_summary?.session_and_market_condition_and_direction_and_trade_direction?.find(
      (r) => r.key === key
    );
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    slice.kind === "session_regime_direction_short_trade_direction" &&
    slice.session_id &&
    slice.market_condition &&
    slice.candle_direction &&
    slice.direction
  ) {
    const key = `${slice.session_id}|${slice.market_condition}|${slice.candle_direction}|${slice.direction}`;
    const row = node.regime_summary?.session_and_market_condition_and_direction_short_and_trade_direction?.find(
      (r) => r.key === key
    );
    if (row) {
      return {
        sample: row.sample_size ?? scopeN,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
    const nested = cohortHeadlineFromNode(node);
    if (
      nested.sample != null ||
      nested.winRate != null ||
      nested.totalPnl != null ||
      nested.expectancy != null
    ) {
      return {
        sample: nested.sample ?? scopeN,
        winRate: nested.winRate,
        totalPnl: nested.totalPnl,
        expectancy: nested.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    slice.kind === "candle_direction_trade_direction" &&
    slice.candle_direction &&
    slice.direction
  ) {
    const key = `${slice.candle_direction}|${slice.direction}`;
    const row = node.regime_summary?.candle_direction_and_trade_direction?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    slice.kind === "candle_direction_short_trade_direction" &&
    slice.candle_direction &&
    slice.direction
  ) {
    const key = `${slice.candle_direction}|${slice.direction}`;
    const row = node.regime_summary?.candle_direction_short_and_trade_direction?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  if (
    slice.kind === "regime_direction_trade_direction" &&
    slice.market_condition &&
    slice.candle_direction &&
    slice.direction
  ) {
    const key = `${slice.market_condition}|${slice.candle_direction}|${slice.direction}`;
    const shortWin = slice.id.startsWith("regime_dir_short_td_");
    const table = shortWin
      ? node.regime_summary?.market_condition_candle_direction_short_and_trade_direction
      : node.regime_summary?.market_condition_candle_direction_and_trade_direction;
    const row = table?.find((r) => r.key === key);
    if (row) {
      return {
        sample: row.sample_size,
        winRate: row.win_rate,
        totalPnl: row.total_pnl,
        expectancy: row.expectancy,
        bestSituation,
        worstSituation,
      };
    }
  }

  const tc = scopeN;
  const matchesScope = (n: number | undefined): boolean =>
    typeof tc === "number" &&
    tc > 0 &&
    typeof n === "number" &&
    Number.isFinite(n) &&
    Math.floor(n) === Math.floor(tc);

  const tdRows = node.regime_summary?.trade_direction;
  if (tdRows?.length === 1 && matchesScope(tdRows[0].sample_size)) {
    const r = tdRows[0];
    return {
      sample: r.sample_size,
      winRate: r.win_rate,
      totalPnl: r.total_pnl,
      expectancy: r.expectancy,
      bestSituation,
      worstSituation,
    };
  }
  const cdRows = node.regime_summary?.candle_direction;
  if (cdRows?.length === 1 && matchesScope(cdRows[0].sample_size)) {
    const r = cdRows[0];
    return {
      sample: r.sample_size,
      winRate: r.win_rate,
      totalPnl: r.total_pnl,
      expectancy: r.expectancy,
      bestSituation,
      worstSituation,
    };
  }

  const mc = node.regime_summary?.market_condition;
  const mcSum = mc ? mc.reduce((s, r) => s + (r.sample_size ?? 0), 0) : undefined;
  // Prefer a single bucket's paired stats (avoid mixing total sample / summed PnL with only the largest bucket's win rate).
  const topMc =
    mc && mc.length ? mc.slice().sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0))[0] : undefined;
  const sample = topMc?.sample_size ?? mcSum;
  const winRate = topMc?.win_rate;
  const totalPnlFromMc =
    mc && mc.length
      ? mc.reduce((sum, r) => sum + (typeof r.total_pnl === "number" && Number.isFinite(r.total_pnl) ? r.total_pnl : 0), 0)
      : undefined;
  const totalPnl =
    topMc != null && typeof topMc.total_pnl === "number" && Number.isFinite(topMc.total_pnl)
      ? topMc.total_pnl
      : totalPnlFromMc != null && mc && mc.some((r) => r.total_pnl != null && Number.isFinite(r.total_pnl))
        ? totalPnlFromMc
        : node.best_conditions?.[0]?.total_pnl ?? undefined;
  const expectancy = topMc?.expectancy ?? node.best_conditions?.[0]?.expectancy ?? undefined;
  return { sample, winRate, totalPnl, expectancy, bestSituation, worstSituation };
}

function isSingleSliceKind(kind: ConditionAnalysisSlice["kind"]): boolean {
  return (
    kind === "all" ||
    kind === "direction" ||
    kind === "regime" ||
    kind === "market_direction" ||
    kind === "market_direction_short" ||
    kind === "candle_direction" ||
    kind === "candle_direction_short" ||
    kind === "session_time"
  );
}

function getSliceCandleWindow(slice: ConditionAnalysisSlice): "long" | "short" | null {
  if (!slice.candle_direction) return null;
  if (
    slice.kind === "market_direction_short" ||
    slice.kind === "candle_direction_short" ||
    slice.kind === "candle_direction_short_trade_direction" ||
    slice.kind === "session_candle_direction_short" ||
    slice.kind === "session_regime_direction_short_trade_direction" ||
    slice.id.startsWith("regime_dir_short_td_") ||
    slice.id.startsWith("sess_mcdstd_")
  ) {
    return "short";
  }
  if (
    slice.kind === "market_direction" ||
    slice.kind === "candle_direction" ||
    slice.kind === "candle_direction_trade_direction" ||
    slice.kind === "regime_direction_trade_direction" ||
    slice.kind === "session_candle_direction" ||
    slice.kind === "session_regime_direction_trade_direction" ||
    slice.id.startsWith("sess_mcd_td_")
  ) {
    return "long";
  }
  return null;
}

function getSliceCriteriaCount(slice: ConditionAnalysisSlice): number {
  let count = 0;
  if (slice.market_condition) count += 1;
  if (slice.candle_direction) count += 1;
  if (slice.direction) count += 1;
  if (slice.session_id) count += 1;
  return count;
}

/** Map segment pick context → slice configurator fields (aligned with Apply logic). */
function sliceContextToConfiguratorFields(ctx: ReportPickSliceContext): {
  configRegime: string;
  configCandleDirection: "" | MarketContextCandleDirection;
  configCandleWindow: "" | "long" | "short";
  configTradeDirection: "" | "long" | "short";
  configSession: string;
} {
  const configRegime = (ctx.market_condition ?? "").trim().toLowerCase();
  const cd = (ctx.candle_direction ?? "").trim().toLowerCase();
  const configCandleDirection =
    cd === "up" || cd === "down" || cd === "flat" ? (cd as MarketContextCandleDirection) : "";
  const td = ctx.trade_direction;
  const configTradeDirection = td === "long" || td === "short" ? td : "";
  const configSession = (ctx.session_id ?? "").trim();

  let configCandleWindow: "" | "long" | "short" = "";
  if (ctx.triple_candle_window === "long" || ctx.triple_candle_window === "short") {
    configCandleWindow = ctx.triple_candle_window;
  } else if (
    ctx.kind === "market_direction_short" ||
    ctx.kind === "candle_direction_short" ||
    ctx.kind === "candle_direction_short_trade_direction"
  ) {
    configCandleWindow = "short";
  } else if (
    ctx.kind === "market_direction" ||
    ctx.kind === "candle_direction" ||
    ctx.kind === "candle_direction_trade_direction"
  ) {
    configCandleWindow = "long";
  } else if (ctx.kind === "session_candle_direction_short" || ctx.kind === "session_regime_direction_short_trade_direction") {
    configCandleWindow = "short";
  } else if (ctx.kind === "session_candle_direction" || ctx.kind === "session_regime_direction_trade_direction") {
    configCandleWindow = "long";
  }

  return { configRegime, configCandleDirection, configCandleWindow, configTradeDirection, configSession };
}

type AppliedConfiguratorFilter = {
  regime: string;
  candleDirection: "" | MarketContextCandleDirection;
  candleWindow: "" | "long" | "short";
  tradeDirection: "" | "long" | "short";
  session: string;
};

function configuratorCriteriaCountFromFields(
  regime: string,
  candleDirection: "" | MarketContextCandleDirection,
  tradeDirection: "" | "long" | "short",
  session: string
): number {
  return (
    (regime.trim() ? 1 : 0) +
    (candleDirection ? 1 : 0) +
    (tradeDirection ? 1 : 0) +
    (session.trim() ? 1 : 0)
  );
}

function formatConfiguratorFilterSummary(
  filter: AppliedConfiguratorFilter,
  candleTimeframe?: string | null
): string {
  const parts: string[] = [];
  if (filter.session.trim()) parts.push(filter.session.trim().replace(/_/g, " "));
  if (filter.regime.trim()) parts.push(filter.regime.trim());
  if (filter.candleDirection) {
    const bars =
      filter.candleWindow === "short"
        ? formatDirectionBarsWindow("short", candleTimeframe)
        : filter.candleWindow === "long"
          ? formatDirectionBarsWindow("long", candleTimeframe)
          : "";
    parts.push(bars ? `${filter.candleDirection} (${bars})` : filter.candleDirection);
  }
  if (filter.tradeDirection) parts.push(filter.tradeDirection);
  return parts.join(" · ");
}

function sortConfiguratorMatchingSlices(a: ConditionAnalysisSlice, b: ConditionAnalysisSlice): number {
  const ac = getSliceCriteriaCount(a);
  const bc = getSliceCriteriaCount(b);
  if (ac !== bc) return ac - bc;
  const cat = getSliceCategoryLabel(a.kind).localeCompare(getSliceCategoryLabel(b.kind), undefined, {
    sensitivity: "base",
  });
  if (cat !== 0) return cat;
  return getDisplaySliceName(a).localeCompare(getDisplaySliceName(b), undefined, { sensitivity: "base" });
}

function findBestMatchingConfiguratorSlice(
  slices: ConditionAnalysisSlice[],
  configRegime: string,
  configCandleDirection: "" | MarketContextCandleDirection,
  configCandleWindow: "" | "long" | "short",
  configTradeDirection: "" | "long" | "short",
  configSession: string
): ConditionAnalysisSlice | null {
  const cr = configRegime.trim().toLowerCase();
  const cc = configCandleDirection;
  const cw = configCandleWindow;
  const ct = configTradeDirection;
  const cs = configSession.trim().toLowerCase();
  const selectedCriteriaCount = configuratorCriteriaCountFromFields(cr, cc, ct, cs);
  if (selectedCriteriaCount === 0) return null;

  const candidates = findMatchingConfiguratorSlices(
    slices,
    configRegime,
    configCandleDirection,
    configCandleWindow,
    configTradeDirection,
    configSession
  );

  if (!candidates.length) return null;

  const scored = candidates
    .map((s) => {
      let matched = 0;
      if (cr && (s.market_condition ?? "").toLowerCase() === cr) matched += 1;
      if (ct && s.direction === ct) matched += 1;
      if (cc && s.candle_direction === cc) matched += 1;
      if (cw) {
        const w = getSliceCandleWindow(s);
        if (w === cw) matched += 1;
      }
      if (cs && (s.session_id ?? "").trim().toLowerCase() === cs) matched += 1;
      return { s, matched };
    })
    .sort((a, b) => {
      const d = b.matched - a.matched;
      if (d !== 0) return d;
      const aCrit = getSliceCriteriaCount(a.s);
      const bCrit = getSliceCriteriaCount(b.s);
      const aDelta = Math.abs(aCrit - selectedCriteriaCount);
      const bDelta = Math.abs(bCrit - selectedCriteriaCount);
      if (aDelta !== bDelta) return aDelta - bDelta;
      return bCrit - aCrit;
    });

  return scored[0]?.s ?? null;
}

function findMatchingConfiguratorSlices(
  slices: ConditionAnalysisSlice[],
  configRegime: string,
  configCandleDirection: "" | MarketContextCandleDirection,
  configCandleWindow: "" | "long" | "short",
  configTradeDirection: "" | "long" | "short",
  configSession: string
): ConditionAnalysisSlice[] {
  const cr = configRegime.trim().toLowerCase();
  const cc = configCandleDirection;
  const cw = configCandleWindow;
  const ct = configTradeDirection;
  const cs = configSession.trim().toLowerCase();
  if (configuratorCriteriaCountFromFields(cr, cc, ct, cs) === 0) return [];

  const candidates = slices.filter((s) => {
    if (s.kind === "all") return false;
    if (cr && (s.market_condition ?? "").toLowerCase() !== cr) return false;
    if (ct && s.direction !== ct) return false;
    if (cc && s.candle_direction !== cc) return false;
    if (cw) {
      const w = getSliceCandleWindow(s);
      if (!w || w !== cw) return false;
    }
    if (cs) {
      const sid = (s.session_id ?? "").trim().toLowerCase();
      if (!sid || sid !== cs) return false;
    }
    return true;
  });

  return [...candidates].sort(sortConfiguratorMatchingSlices);
}

function PivotTable(props: {
  slices: ConditionAnalysisSlice[];
  selectedId: string;
  onSelect: (id: string) => void;
  textColor: string;
  subtleText: string;
  borderColor: string;
  sessionMarginalSourceNode?: ConditionAnalysisNode | null;
  candleTimeframe?: string | null;
}): React.ReactElement {
  type PivotSortColumn = "slice" | "category" | "sample" | "winRate" | "expectancy" | "totalPnl" | "best" | "worst";
  const cardBg = useColorModeValue("white", REPORTS_DETAIL_SURFACE_DARK);
  const secondaryTextColor = useColorModeValue("gray.500", REPORTS_DETAIL_MUTED_DARK);
  const bodyTextColor = useColorModeValue("gray.500", REPORTS_DETAIL_MUTED_DARK);
  const buttonHoverBg = useColorModeValue("gray.100", "var(--muted)");
  const selectedRowBg = useColorModeValue("rgba(56, 187, 209, 0.08)", "rgba(56, 187, 209, 0.16)");
  const selectedRowHoverBg = useColorModeValue("rgba(56, 187, 209, 0.12)", "rgba(56, 187, 209, 0.22)");
  const [sortState, setSortState] = useState<{ col: PivotSortColumn; dir: "asc" | "desc" } | null>(null);

  const onSortClick = useCallback((col: PivotSortColumn) => {
    setSortState((prev) => {
      if (!prev || prev.col !== col) {
        const defaultDir: "asc" | "desc" =
          col === "sample" || col === "winRate" || col === "expectancy" || col === "totalPnl" ? "desc" : "asc";
        return { col, dir: defaultDir };
      }
      return { col, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }, []);

  const headlineOpts = useMemo(
    (): SliceHeadlineOptions => ({ sessionMarginalSourceNode: props.sessionMarginalSourceNode }),
    [props.sessionMarginalSourceNode]
  );

  const sortedSlices = useMemo(() => {
    if (!sortState) return props.slices;
    const factor = sortState.dir === "asc" ? 1 : -1;
    const rows = [...props.slices];
    rows.sort((a, b) => {
      const ha = sliceHeadline(a, headlineOpts);
      const hb = sliceHeadline(b, headlineOpts);
      let cmp = 0;
      switch (sortState.col) {
        case "slice":
          cmp = getDisplaySliceName(a).localeCompare(getDisplaySliceName(b), undefined, { sensitivity: "base" });
          break;
        case "category":
          cmp = getSliceCategoryLabel(a.kind).localeCompare(getSliceCategoryLabel(b.kind), undefined, {
            sensitivity: "base",
          });
          break;
        case "sample":
          cmp = (ha.sample ?? Number.NEGATIVE_INFINITY) - (hb.sample ?? Number.NEGATIVE_INFINITY);
          break;
        case "winRate":
          cmp = (ha.winRate ?? Number.NEGATIVE_INFINITY) - (hb.winRate ?? Number.NEGATIVE_INFINITY);
          break;
        case "expectancy":
          cmp = (ha.expectancy ?? Number.NEGATIVE_INFINITY) - (hb.expectancy ?? Number.NEGATIVE_INFINITY);
          break;
        case "totalPnl":
          cmp = (ha.totalPnl ?? Number.NEGATIVE_INFINITY) - (hb.totalPnl ?? Number.NEGATIVE_INFINITY);
          break;
        case "best":
          cmp = (ha.bestSituation ?? "").localeCompare(hb.bestSituation ?? "", undefined, { sensitivity: "base" });
          break;
        case "worst":
          cmp = (ha.worstSituation ?? "").localeCompare(hb.worstSituation ?? "", undefined, { sensitivity: "base" });
          break;
      }
      if (cmp === 0) {
        cmp = getDisplaySliceName(a).localeCompare(getDisplaySliceName(b), undefined, { sensitivity: "base" });
      }
      return cmp * factor;
    });
    return rows;
  }, [headlineOpts, props.slices, sortState]);

  return (
    <Box
      overflowX="auto"
      overflowY="auto"
      borderTopLeftRadius="12px"
      borderTopRightRadius="12px"
      border="1px solid"
      borderColor={props.borderColor}
      maxH="380px"
      sx={{ WebkitOverflowScrolling: "touch" }}
    >
      <Table variant="simple" color={props.textColor} w="max-content" minW="max-content" tableLayout="auto">
        <Thead bg={cardBg} borderTopLeftRadius="12px" borderTopRightRadius="12px" position="sticky" top={0} zIndex={2}>
          <Tr>
            <Th
              color={secondaryTextColor}
              minW="max-content"
              whiteSpace="nowrap"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              borderTopLeftRadius="12px"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("slice")}
            >
              <HStack spacing={1} flexWrap="nowrap" whiteSpace="nowrap">
                <Text fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" whiteSpace="nowrap">
                  Slice
                </Text>
                <FaSort size={10} />
              </HStack>
            </Th>
            <Th
              color={secondaryTextColor}
              minW="max-content"
              whiteSpace="nowrap"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("category")}
            >
              <HStack spacing={1} flexWrap="nowrap" whiteSpace="nowrap">
                <Text fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" whiteSpace="nowrap">
                  Category
                </Text>
                <FaSort size={10} />
              </HStack>
            </Th>
            <Th
              color={secondaryTextColor}
              isNumeric
              minW="max-content"
              whiteSpace="nowrap"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("sample")}
            >
              <Tooltip label="All trades in the slice row, including scratch/breakeven." hasArrow openDelay={350} placement="top">
                <HStack spacing={1} justify="flex-end" flexWrap="nowrap" whiteSpace="nowrap">
                  <Box
                    as="span"
                    borderBottomWidth="1px"
                    borderStyle="dotted"
                    borderColor="currentColor"
                    cursor="help"
                    whiteSpace="nowrap"
                  >
                    Sample
                  </Box>
                  <FaSort size={10} />
                </HStack>
              </Tooltip>
            </Th>
            <Th
              color={secondaryTextColor}
              isNumeric
              minW="max-content"
              whiteSpace="nowrap"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("winRate")}
            >
              <Tooltip label={WIN_RATE_COLUMN_TOOLTIP} hasArrow openDelay={350} placement="top" maxW="280px">
                <HStack spacing={1} justify="flex-end" flexWrap="nowrap" whiteSpace="nowrap">
                  <Box
                    as="span"
                    borderBottomWidth="1px"
                    borderStyle="dotted"
                    borderColor="currentColor"
                    cursor="help"
                    whiteSpace="nowrap"
                  >
                    Win rate
                  </Box>
                  <FaSort size={10} />
                </HStack>
              </Tooltip>
            </Th>
            <Th
              color={secondaryTextColor}
              isNumeric
              minW="max-content"
              whiteSpace="nowrap"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("expectancy")}
            >
              <Tooltip
                label="Average expected return per trade for the slice."
                hasArrow
                openDelay={350}
                placement="top"
              >
                <HStack spacing={1} justify="flex-end" flexWrap="nowrap" whiteSpace="nowrap">
                  <Box
                    as="span"
                    borderBottomWidth="1px"
                    borderStyle="dotted"
                    borderColor="currentColor"
                    cursor="help"
                    whiteSpace="nowrap"
                  >
                    Expectancy
                  </Box>
                  <FaSort size={10} />
                </HStack>
              </Tooltip>
            </Th>
            <Th
              color={secondaryTextColor}
              isNumeric
              minW="max-content"
              whiteSpace="nowrap"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("totalPnl")}
            >
              <Tooltip
                label="Total profit and loss across all trades in the slice."
                hasArrow
                openDelay={350}
                placement="top"
              >
                <HStack spacing={1} justify="flex-end" flexWrap="nowrap" whiteSpace="nowrap">
                  <Box
                    as="span"
                    borderBottomWidth="1px"
                    borderStyle="dotted"
                    borderColor="currentColor"
                    cursor="help"
                    whiteSpace="nowrap"
                  >
                    Total PnL
                  </Box>
                  <FaSort size={10} />
                </HStack>
              </Tooltip>
            </Th>
            <Th
              color={secondaryTextColor}
              minW="max-content"
              whiteSpace="nowrap"
              textAlign="right"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("best")}
            >
              <Tooltip
                label="Best-performing situation example for the selected slice."
                hasArrow
                openDelay={350}
                placement="top"
              >
                <HStack spacing={1} justify="flex-end" flexWrap="nowrap" whiteSpace="nowrap">
                  <Box
                    as="span"
                    borderBottomWidth="1px"
                    borderStyle="dotted"
                    borderColor="currentColor"
                    cursor="help"
                    whiteSpace="nowrap"
                  >
                    Top best situation
                  </Box>
                  <FaSort size={10} />
                </HStack>
              </Tooltip>
            </Th>
            <Th
              color={secondaryTextColor}
              minW="max-content"
              whiteSpace="nowrap"
              textAlign="right"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              borderTopRightRadius="12px"
              cursor="pointer"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => onSortClick("worst")}
            >
              <Tooltip
                label="Worst-performing situation example for the selected slice."
                hasArrow
                openDelay={350}
                placement="top"
              >
                <HStack spacing={1} justify="flex-end" flexWrap="nowrap" whiteSpace="nowrap">
                  <Box
                    as="span"
                    borderBottomWidth="1px"
                    borderStyle="dotted"
                    borderColor="currentColor"
                    cursor="help"
                    whiteSpace="nowrap"
                  >
                    Top worst situation
                  </Box>
                  <FaSort size={10} />
                </HStack>
              </Tooltip>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedSlices.map((s) => {
            const h = sliceHeadline(s, headlineOpts);
            const isSelected = s.id === props.selectedId;
            return (
              <Tr
                key={s.id}
                bg={isSelected ? selectedRowBg : undefined}
                _hover={{ bg: isSelected ? selectedRowHoverBg : buttonHoverBg }}
                cursor="pointer"
                onClick={() => props.onSelect(s.id)}
              >
                <Td>
                  <Text
                    fontSize="sm"
                    color={bodyTextColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                  >
                    {getDisplaySliceName(s, props.candleTimeframe)}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    bgGradient={getSliceCategoryBadgeGradient(s)}
                    color="white"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    textTransform="uppercase"
                    px="10px"
                    py="3px"
                    borderRadius="6px"
                    whiteSpace="nowrap"
                  >
                    {getSliceCategoryLabel(s.kind, props.candleTimeframe)}
                  </Badge>
                </Td>
                <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                  {fmtInt(h.sample)}
                </Td>
                <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                  {fmtPct(h.winRate)}
                </Td>
                <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                  {fmtNum(h.expectancy)}
                </Td>
                <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                  {h.totalPnl == null || !Number.isFinite(h.totalPnl) ? (
                    "—"
                  ) : (
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight="medium"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      color={h.totalPnl >= 0 ? "#06E363" : "#E00101"}
                    >
                      {fmtNum(h.totalPnl)}
                    </Text>
                  )}
                </Td>
                <Td fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor}>
                  <Box
                    as="span"
                    title={h.bestSituation ?? ""}
                    display="block"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    textAlign="right"
                  >
                    {h.bestSituation ?? "—"}
                  </Box>
                </Td>
                <Td fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor}>
                  <Box
                    as="span"
                    title={h.worstSituation ?? ""}
                    display="block"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    textAlign="right"
                  >
                    {h.worstSituation ?? "—"}
                  </Box>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function ConfiguredSliceResultTable(props: {
  slice: ConditionAnalysisSlice;
  title: string;
  textColor: string;
  subtleText: string;
  borderColor: string;
  panelBg: string;
  emptyPanelBg: string;
  sessionMarginalSourceNode?: ConditionAnalysisNode | null;
  /** Add the headline slice as a `segment` simulator pick (same cohort as this results row). */
  onAddToSimulator?: () => void;
  isSlicePicked?: boolean;
  candleTimeframe?: string | null;
}): React.ReactElement {
  const row = sliceHeadline(props.slice, {
    sessionMarginalSourceNode: props.sessionMarginalSourceNode,
  });
  const rowHoverBg = useColorModeValue("rgba(56, 187, 209, 0.08)", "rgba(56, 187, 209, 0.18)");
  const bodyTextColor = useColorModeValue("gray.500", REPORTS_DETAIL_MUTED_DARK);
  const pnlColor =
    row.totalPnl == null || !Number.isFinite(row.totalPnl)
      ? bodyTextColor
      : row.totalPnl >= 0
        ? "#06E363"
        : "#E00101";
  const sliceCardTitleColor = useColorModeValue("gray.500", REPORTS_DETAIL_MUTED_DARK);

  return (
    <Box mb={6} borderWidth="1px" borderRadius="6px" borderColor={props.borderColor} bg={props.emptyPanelBg} p={4}>
      <Text
        fontSize="xs"
        fontWeight="medium"
        color={sliceCardTitleColor}
        mb={3}
        fontFamily="'IBM Plex Sans', sans-serif"
        textTransform="uppercase"
        lineHeight="1"
      >
        {props.title}
      </Text>
      <Box
        maxH="280px"
        overflowY="auto"
        overflowX="auto"
        borderWidth="1px"
        borderRadius="6px"
        borderColor={props.borderColor}
        bg={props.panelBg}
      >
        <Table variant="simple" minW={props.onAddToSimulator ? "1032px" : "900px"} size="sm">
          <Thead position="sticky" top={0} zIndex={1} bg={props.panelBg} boxShadow="0 1px 0 0 rgba(0,0,0,0.06)">
            <Tr>
              <Th color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Slice
              </Th>
              {props.onAddToSimulator ? (
                <Th
                  color={props.subtleText}
                  w="148px"
                  minW="148px"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  whiteSpace="nowrap"
                >
                  Add to Simulator
                </Th>
              ) : null}
              <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Sample
              </Th>
              <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Win rate
              </Th>
              <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Expectancy
              </Th>
              <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Total PnL
              </Th>
              <Th color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Top best situation
              </Th>
              <Th color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                Top worst situation
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr _hover={{ bg: rowHoverBg }}>
              <Td fontSize="sm" color={bodyTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                {getDisplaySliceName(props.slice, props.candleTimeframe)}
              </Td>
              {props.onAddToSimulator ? (
                <Td px={3} py={1} verticalAlign="middle" w="148px" minW="148px">
                  <div data-market-context-simulator-pick-cell="" className="inline-flex items-center">
                  <SimulatorPickActionCell
                    picked={Boolean(props.isSlicePicked)}
                    addAriaLabel="Add to Simulator"
                    onAdd={() => props.onAddToSimulator?.()}
                  />
                  </div>
                </Td>
              ) : null}
              <Td isNumeric fontSize="sm" color={bodyTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                {fmtInt(row.sample)}
              </Td>
              <Td isNumeric fontSize="sm" color={bodyTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                {fmtPct(row.winRate)}
              </Td>
              <Td isNumeric fontSize="sm" color={bodyTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                {fmtNum(row.expectancy)}
              </Td>
              <Td isNumeric fontSize="sm" color={pnlColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                {fmtNum(row.totalPnl)}
              </Td>
              <Td fontSize="xs" color={bodyTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                {row.bestSituation ?? "—"}
              </Td>
              <Td fontSize="xs" color={bodyTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                {row.worstSituation ?? "—"}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

function TablesOmittedForSliceContextPanel(props: {
  entries: ConditionAnalysisTableOmittedEntry[];
  borderColor: string;
  emptyPanelBg: string;
  subtleText: string;
  textColor: string;
}): React.ReactElement {
  return (
    <Box mb={4} borderWidth="1px" borderRadius="6px" borderColor={props.borderColor} bg={props.emptyPanelBg} p={3}>
      <Text fontSize="sm" fontWeight="semibold" color={props.textColor} mb={2}>
        Tables omitted for this slice
      </Text>
      <Text fontSize="xs" color={props.subtleText} mb={2}>
        Hover a table id (when shown) for the full note. Same data lives under{" "}
        <Text as="span" fontFamily="mono">
          regime_summary.tables_omitted_for_slice_context
        </Text>
        .
      </Text>
      <Stack spacing={2} align="stretch">
        {props.entries.map((e, i) => (
          <HStack key={i} align="flex-start" spacing={2}>
            {e.table_key ? (
              <Tooltip label={e.message} hasArrow openDelay={400} placement="top">
                <Box
                  as="span"
                  fontFamily="mono"
                  fontSize="xs"
                  color="teal.300"
                  cursor="help"
                  borderBottomWidth="1px"
                  borderStyle="dotted"
                  borderColor="currentColor"
                  flexShrink={0}
                >
                  {e.table_key}
                </Box>
              </Tooltip>
            ) : null}
            <Text fontSize="xs" color={props.textColor} flex={1} whiteSpace="pre-wrap">
              {e.message}
            </Text>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
}

function UniformSegmentSummaryPanel(props: {
  ctx: ConditionAnalysisUniformSegmentContext;
  borderColor: string;
  panelBg: string;
  emptyPanelBg: string;
  subtleText: string;
  textColor: string;
}): React.ReactElement {
  const agg: ConditionAnalysisSegmentAggregate | undefined = props.ctx.segment_aggregate;
  const bodyTextColor = useColorModeValue("gray.500", REPORTS_DETAIL_MUTED_DARK);
  return (
    <Box mb={4} borderWidth="1px" borderRadius="6px" borderColor={props.borderColor} bg={props.emptyPanelBg} p={3}>
      <Text fontSize="sm" fontWeight="semibold" color={props.textColor} mb={1}>
        Segment context
      </Text>
      <Text fontSize="xs" color={props.subtleText} mb={3}>
        Marginal rollups match the whole slice (one row each with identical metrics). Headline context is below; use
        best/worst conditions and situations in the tabs for within-slice spread (session, ADX bands, etc.).
      </Text>
      {props.ctx.ui_hint ? (
        <Box
          mb={3}
          borderLeftWidth="3px"
          borderColor="teal.400"
          pl={3}
          py={1}
        >
          <Text fontSize="xs" color={props.subtleText} mb={1} fontWeight="semibold" textTransform="uppercase">
            From backend (ui_hint)
          </Text>
          <Text fontSize="sm" color={props.textColor} whiteSpace="pre-wrap">
            {props.ctx.ui_hint}
          </Text>
        </Box>
      ) : null}
      {agg ? (
        <Box borderWidth="1px" borderRadius="6px" borderColor={props.borderColor} bg={props.panelBg} overflow="hidden">
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} p={3}>
            {[
              ["market_condition", agg.market_condition],
              ["candle_direction", agg.candle_direction],
              ["trade_direction", agg.trade_direction],
              ["triple_key", agg.triple_key],
            ].map(([label, val]) =>
              val ? (
                <Box key={String(label)}>
                  <Text fontSize="xs" color={props.subtleText} mb={1} fontFamily="mono">
                    {label}
                  </Text>
                  <Text fontSize="sm" color={props.textColor} fontWeight="medium">
                    {val}
                  </Text>
                </Box>
              ) : null
            )}
          </SimpleGrid>
          <Box borderTopWidth="1px" borderColor={props.borderColor} px={3} py={2}>
            <Table variant="simple" size="sm" color={props.textColor}>
              <Thead bg={props.panelBg}>
                <Tr>
                  <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                    <Tooltip label="All trades in this segment, including scratch/breakeven." hasArrow openDelay={350} placement="top">
                      <Box as="span" borderBottomWidth="1px" borderStyle="dotted" borderColor="currentColor" cursor="help">
                        Sample
                      </Box>
                    </Tooltip>
                  </Th>
                  <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                    <Tooltip label={WIN_RATE_COLUMN_TOOLTIP} hasArrow openDelay={350} placement="top" maxW="280px">
                      <Box as="span" borderBottomWidth="1px" borderStyle="dotted" borderColor="currentColor" cursor="help">
                        Win rate
                      </Box>
                    </Tooltip>
                  </Th>
                  <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                    Expectancy
                  </Th>
                  <Th color={props.subtleText} isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                    Total PnL
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                    {fmtInt(agg.sample_size)}
                  </Td>
                  <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                    {fmtPct(agg.win_rate)}
                  </Td>
                  <Td isNumeric fontFamily="'IBM Plex Sans', sans-serif" fontSize="sm" color={bodyTextColor} fontWeight="medium">
                    {fmtNum(agg.expectancy)}
                  </Td>
                  <Td
                    isNumeric
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontSize="sm"
                    color={
                      agg.total_pnl == null || !Number.isFinite(agg.total_pnl)
                        ? bodyTextColor
                        : agg.total_pnl >= 0
                          ? "#06E363"
                          : "#E00101"
                    }
                    fontWeight="medium"
                  >
                    {fmtPnl(agg.total_pnl)}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Box>
      ) : (
        <Text fontSize="sm" color={props.subtleText}>
          No segment_aggregate in snapshot.
        </Text>
      )}
    </Box>
  );
}

function RegimeSummaryContextNotesPanel(props: {
  node: ConditionAnalysisNode;
  /** Trade Analysis reports omit the uniform-segment explainer panel. */
  showUniformSegmentContext?: boolean;
  borderColor: string;
  panelBg: string;
  emptyPanelBg: string;
  subtleText: string;
  textColor: string;
}): React.ReactElement | null {
  const rs = props.node.regime_summary;
  if (!rs) return null;

  const rb = props.node.regime_breakdown;
  const redundant = rs.uniform_segment_context?.marginal_tables_redundant === true;
  const hasNotes = (rs.notes?.length ?? 0) > 0;
  const tablesOmitted = rs.tables_omitted_for_slice_context;
  const hasTablesOmittedUx = (tablesOmitted?.length ?? 0) > 0;

  const hasShortTables =
    (rs.candle_direction_short?.length ?? 0) > 0 ||
    (rs.candle_direction_short_and_trade_direction?.length ?? 0) > 0 ||
    (rs.market_condition_candle_direction_short_and_trade_direction?.length ?? 0) > 0 ||
    (mergedSessionMarginalRows(rs.session_and_candle_direction_short, rb?.by_session_and_candle_direction_short)
      ?.length ?? 0) > 0 ||
    (mergedSessionMarginalRows(
      rs.session_and_market_condition_and_direction_short_and_trade_direction,
      rb?.by_session_and_market_condition_and_direction_short_and_trade_direction
    )?.length ?? 0) > 0;

  const showShortWindowNotice = !redundant && !hasShortTables;
  if (!hasNotes && !redundant && !hasTablesOmittedUx && !showShortWindowNotice) {
    return null;
  }

  return (
    <Box mt={4}>
      {hasTablesOmittedUx && tablesOmitted ? (
        <TablesOmittedForSliceContextPanel
          entries={tablesOmitted}
          borderColor={props.borderColor}
          emptyPanelBg={props.emptyPanelBg}
          subtleText={props.subtleText}
          textColor={props.textColor}
        />
      ) : null}

      {props.showUniformSegmentContext === true && redundant && rs.uniform_segment_context ? (
        <UniformSegmentSummaryPanel
          ctx={rs.uniform_segment_context}
          borderColor={props.borderColor}
          panelBg={props.panelBg}
          emptyPanelBg={props.emptyPanelBg}
          subtleText={props.subtleText}
          textColor={props.textColor}
        />
      ) : null}

      {showShortWindowNotice ? (
        <Box mt={3} borderWidth="1px" borderRadius="6px" borderColor={props.borderColor} bg={props.emptyPanelBg} p={3}>
          <Text fontSize="sm" fontWeight="semibold" color={props.textColor} mb={1}>
            Short-window direction not available
          </Text>
          <Text fontSize="xs" color={props.subtleText}>
            Not enough entry data to classify short-window direction (direction_short is unknown for most trades in this report).
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

export function ConditionAnalysisSection(props: {
  reportId?: string;
  reportType?: string;
  /** Report candle TF — scales long/short direction bar labels (v3 lookbacks). */
  candleTimeframe?: string | null;
  inputSnapshot: unknown;
  /** Full `analysis_evidence` from GET report (when stats.condition_analysis is slimmed). */
  analysisEvidence?: unknown;
  textColor: string;
  subtleText: string;
  evidencePreBg: string;
  evidencePreBorder: string;
  /** Demo / gated: keep Add-to-simulator column visible but locked. */
  simulatorPicksDisabled?: boolean;
  simulatorPicksDisabledReason?: string;
  managementCatalog?: ManagementSegmentCatalogEntry[];
  managementCandidates?: ManagementCandidate[];
  managementLookup?: ManagementCatalogLookup;
  onPreviewManagement?: (request: ManagementPreviewRequest) => void | Promise<void>;
  onOpenManagementInSimulator?: (args: { segmentKey: string; policy: PolicyTreeV2 }) => void;
  managementPreviewLoadingKey?: string | null;
  /** Overview finding link — focus slice configurator when user clicks View in Market tab. */
  marketContextFocus?: import("../../../utils/marketContextNavigation").MarketContextNavigationTarget | null;
  marketContextFocusNonce?: number;
  /** Called when the user clears configurator state so overview navigation does not re-apply. */
  onDismissMarketContextFocus?: () => void;
  onPreviewConditionBucket?: (
    row: ConditionAnalysisBucketRow,
    sliceContext: ReportPickSliceContext | null,
    analysisScope?: ConditionAnalysisScope | null,
    label?: string
  ) => void;
  onPreviewConditionSituation?: (
    row: ConditionAnalysisSituationRow,
    sliceContext: ReportPickSliceContext | null,
    analysisScope?: ConditionAnalysisScope | null,
    label?: string
  ) => void;
  onPreviewConditionSlice?: (
    slice: ConditionAnalysisSlice,
    sliceContext: ReportPickSliceContext | null,
    label: string,
    expectedSampleSize?: number
  ) => void;
  onPreviewConditionSummaryRow?: (
    tableKey: string,
    row: ConditionAnalysisSummaryRow,
    sliceContext: ReportPickSliceContext | null,
    label: string
  ) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  /** Demo: omit “Show all” under the slice pivot table. */
  hideSliceTableCollapseControls?: boolean;
}): React.ReactElement | null {
  const marketContextSectionRef = useRef<HTMLDivElement>(null);
  const lastAppliedMarketContextFocusNonceRef = useRef(0);
  const borderColor = REPORT_DETAIL_CARD_BORDER;
  const panelBg = useColorModeValue("white", REPORTS_DETAIL_SURFACE_DARK);
  const emptyPanelBg = useColorModeValue("gray.50", REPORTS_DETAIL_SURFACE_DARK);
  const neutralOutlineBorderColor = useColorModeValue("gray.300", REPORTS_DETAIL_BORDER_DARK);

  const conditionAnalysisSource = useMemo(() => {
    if (!props.analysisEvidence) return props.inputSnapshot;
    if (props.inputSnapshot && typeof props.inputSnapshot === "object" && !Array.isArray(props.inputSnapshot)) {
      return {
        ...(props.inputSnapshot as Record<string, unknown>),
        analysis_evidence: props.analysisEvidence,
      };
    }
    return {
      analysis_evidence: props.analysisEvidence,
    };
  }, [props.analysisEvidence, props.inputSnapshot]);
  const slices = useMemo(
    () => buildConditionAnalysisSlices(conditionAnalysisSource, props.candleTimeframe),
    [conditionAnalysisSource, props.candleTimeframe]
  );
  const directionShortBarsLabel = formatDirectionBarsWindow("short", props.candleTimeframe);
  const directionLongBarsLabel = formatDirectionBarsWindow("long", props.candleTimeframe);
  const [savedPicks, setSavedPicks] = useState<ReportPick[]>(() => (props.reportId ? readPickListForReport(props.reportId) : []));
  useEffect(() => {
    if (!props.reportId) return;
    const sync = () => setSavedPicks(readPickListForReport(props.reportId!));
    sync();
    window.addEventListener(REPORT_PICK_LIST_CHANGED_EVENT, sync as any);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(REPORT_PICK_LIST_CHANGED_EVENT, sync as any);
      window.removeEventListener("storage", sync);
    };
  }, [props.reportId]);

  const [selectedId, setSelectedId] = useState<string>("all");
  const [configuredSliceId, setConfiguredSliceId] = useState<string | null>(null);
  const [appliedConfiguratorFilter, setAppliedConfiguratorFilter] =
    useState<AppliedConfiguratorFilter | null>(null);
  const [configRegime, setConfigRegime] = useState<string>("");
  const [configCandleDirection, setConfigCandleDirection] = useState<"" | MarketContextCandleDirection>("");
  const [configCandleWindow, setConfigCandleWindow] = useState<"" | "long" | "short">("");
  const [configTradeDirection, setConfigTradeDirection] = useState<"" | "long" | "short">("");
  const [configSession, setConfigSession] = useState<string>("");
  /** Catalog `regime.market_condition.enum_values` (v3) for the slice configurator picker. */
  const { catalogFields } = useTradePolicyFilterCatalogFields();

  const configuratorFilterActive = appliedConfiguratorFilter != null;

  const configuredSlice = useMemo(
    () => (configuredSliceId ? slices.find((s) => s.id === configuredSliceId) ?? null : null),
    [configuredSliceId, slices]
  );
  const selectedTableSlice = useMemo(
    () => slices.find((s) => s.id === selectedId) ?? slices.find((s) => s.kind === "all") ?? slices[0] ?? null,
    [slices, selectedId]
  );
  const selected = configuredSlice ?? selectedTableSlice;
  const selectedNode = selected?.node ?? null;
  const allTradesSlice = useMemo(() => slices.find((s) => s.kind === "all") ?? null, [slices]);
  const allTradesNode = allTradesSlice?.node ?? null;

  const configuratorCohortTotal = useMemo(() => {
    if (!allTradesNode) return undefined;
    const sample = cohortHeadlineFromNode(allTradesNode).sample;
    return sample != null && Number.isFinite(sample) ? Math.floor(sample) : undefined;
  }, [allTradesNode]);

  const regimeMarginalCounts = useMemo(
    () => marginalSampleByLowerKey(allTradesNode?.regime_summary?.market_condition),
    [allTradesNode]
  );
  const tradeDirMarginalCounts = useMemo(
    () => marginalSampleByLowerKey(allTradesNode?.regime_summary?.trade_direction),
    [allTradesNode]
  );
  const candleLongMarginalCounts = useMemo(
    () => marginalSampleByLowerKey(allTradesNode?.regime_summary?.candle_direction),
    [allTradesNode]
  );
  const candleShortMarginalCounts = useMemo(
    () => marginalSampleByLowerKey(allTradesNode?.regime_summary?.candle_direction_short),
    [allTradesNode]
  );

  const availableRegimeSet = useMemo(() => {
    const set = new Set<string>();
    for (const s of slices) {
      const mc = (s.market_condition ?? "").trim().toLowerCase();
      if (mc) set.add(mc);
    }
    return set;
  }, [slices]);

  const regimeConfiguratorOptions = useMemo(() => {
    const values = marketConditionPickerValuesForSelect(catalogFields, configRegime);
    return values.map((value) => {
      const sample = regimeMarginalCounts.get(value) ?? 0;
      const labelBase = formatMarketConditionLabel(value);
      const legacySuffix = isMarketConditionLegacyOnlyValue(value) ? " · legacy" : "";
      return {
        value,
        label: `${labelBase}${legacySuffix} (${fmtInt(sample)})`,
        disabled: !availableRegimeSet.has(value),
      };
    });
  }, [availableRegimeSet, catalogFields, configRegime, regimeMarginalCounts]);
  const availableTradeDirectionSet = useMemo(() => {
    const set = new Set<string>();
    for (const s of slices) {
      if (s.direction === "long" || s.direction === "short") set.add(s.direction);
    }
    return set;
  }, [slices]);
  const availableCandleDirectionSet = useMemo(() => {
    const set = new Set<string>();
    for (const s of slices) {
      const dir = s.candle_direction;
      if (dir === "up" || dir === "down" || dir === "flat") {
        const w = getSliceCandleWindow(s);
        if (w) set.add(`${w}:${dir}`);
      }
    }
    return set;
  }, [slices]);

  const reportSessionMarginalRows = useMemo(
    () => allTradesNode?.regime_summary?.session ?? null,
    [allTradesNode]
  );

  const sessionConfiguratorOptions = useMemo(() => {
    const rows = reportSessionMarginalRows ?? [];
    return [...rows].sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0));
  }, [reportSessionMarginalRows]);

  const sliceConfiguratorAnyCountSuffix = useMemo(
    () => (configuratorCohortTotal != null ? ` (${fmtInt(configuratorCohortTotal)})` : ""),
    [configuratorCohortTotal],
  );

  const sliceConfiguratorAnyRegimeLabel = useMemo(
    () => `Any regime${sliceConfiguratorAnyCountSuffix}`,
    [sliceConfiguratorAnyCountSuffix],
  );

  const sliceConfiguratorAnyCandleDirectionLabel = useMemo(
    () => `Any market direction${sliceConfiguratorAnyCountSuffix}`,
    [sliceConfiguratorAnyCountSuffix],
  );

  const sliceConfiguratorAnyTradeDirectionLabel = useMemo(
    () => `Any trade direction${sliceConfiguratorAnyCountSuffix}`,
    [sliceConfiguratorAnyCountSuffix],
  );

  const sliceConfiguratorAnySessionLabel = useMemo(
    () => `Any trading session${sliceConfiguratorAnyCountSuffix}`,
    [sliceConfiguratorAnyCountSuffix],
  );

  const {
    isOpen: isMetricDefinitionsOpen,
    onOpen: onMetricDefinitionsOpen,
    onClose: onMetricDefinitionsClose,
  } = useDisclosure();

  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const metricDefinitionsNode = selectedNode ?? allTradesNode ?? null;
  const metricDefinitionsBucketDefs =
    selectedNode?.market_condition_brackets?.bucket_metric_definitions ??
    allTradesNode?.market_condition_brackets?.bucket_metric_definitions;
  const mergedMetricDefinitions = useMemo(
    () =>
      mergedMetricDefinitionMap(
        metricDefinitionsBucketDefs,
        metricDefinitionsNode,
        props.candleTimeframe
      ),
    [metricDefinitionsBucketDefs, metricDefinitionsNode, props.candleTimeframe],
  );

  useEffect(() => {
    const keys = sortMetricDefinitionKeys(Object.keys(mergedMetricDefinitions));
    if (!keys.length) {
      if (selectedMetric !== null) setSelectedMetric(null);
      return;
    }
    if (!selectedMetric || mergedMetricDefinitions[selectedMetric] == null) {
      setSelectedMetric(keys[0]);
    }
  }, [mergedMetricDefinitions, selectedMetric]);

  const metricDefinitionsRef =
    selectedNode?.market_condition_brackets?.bucket_metric_definitions_ref ??
    allTradesNode?.market_condition_brackets?.bucket_metric_definitions_ref;

  const metricDefinitionRows = useMemo(
    () => buildMetricDefinitionRows(metricDefinitionsBucketDefs, metricDefinitionsNode),
    [metricDefinitionsBucketDefs, metricDefinitionsNode]
  );

  const openMetricDefinitionsAtMetric = useCallback(
    (metric: string) => {
      setSelectedMetric(metric);
      onMetricDefinitionsOpen();
    },
    [onMetricDefinitionsOpen]
  );

  const isSliceFocused =
    configuredSlice != null ||
    (selectedTableSlice != null && selectedTableSlice.kind !== "all");
  /**
   * Matching-slices browse stays on until Exact Open (`configuredSlice`).
   * Row click only seeds the configurator / highlights a row — it must not clear the filter
   * or the exploration table jumps back to the full unfiltered list.
   */
  const drilldownFilterEffective = configuratorFilterActive && configuredSlice == null;
  const isConfiguratorActive = configuratorFilterActive || configuredSlice != null;
  /** Exact slice from Open this slice — hides the full exploration table; keeps exact result + buckets/situations. */
  const isConfiguredCombinedSlice = configuredSlice != null;
  const segmentHeadlineSlice: ConditionAnalysisSlice | null =
    configuredSlice ??
    (selectedTableSlice && selectedTableSlice.kind !== "all" ? selectedTableSlice : null);
  const segmentHeadlineMetrics = useMemo(
    () =>
      segmentHeadlineSlice
        ? sliceHeadline(segmentHeadlineSlice, { sessionMarginalSourceNode: allTradesNode })
        : null,
    [allTradesNode, segmentHeadlineSlice]
  );
  const configuredSliceHeadline = useMemo(
    () =>
      configuredSlice
        ? sliceHeadline(configuredSlice, { sessionMarginalSourceNode: allTradesNode })
        : null,
    [allTradesNode, configuredSlice]
  );
  const configuredSliceHasNoResults = useMemo(() => {
    if (!configuredSlice) return false;
    const sample = configuredSliceHeadline?.sample;
    const hasSample = typeof sample === "number" && Number.isFinite(sample) && sample > 0;
    const node = configuredSlice.node;
    const hasAnyRows =
      (node.best_conditions?.length ?? 0) > 0 ||
      (node.worst_conditions?.length ?? 0) > 0 ||
      (node.market_condition_brackets?.best_situations?.length ?? 0) > 0 ||
      (node.market_condition_brackets?.worst_situations?.length ?? 0) > 0;
    return !hasSample && !hasAnyRows;
  }, [configuredSlice, configuredSliceHeadline]);
  const isFocusedSegmentView = segmentHeadlineSlice != null;
  const isAllTradesExploration =
    selectedTableSlice?.kind === "all" && !configuredSlice && !drilldownFilterEffective;
  /** Context notes (uniform segment / short-window) — only while exploring All trades. */
  const showRegimeSummaryContextNotes =
    Boolean(allTradesNode?.regime_summary) &&
    !isSliceFocused &&
    (drilldownFilterEffective || isAllTradesExploration);

  const mainTableSlices = useMemo(() => {
    if (drilldownFilterEffective && appliedConfiguratorFilter) {
      return findMatchingConfiguratorSlices(
        slices,
        appliedConfiguratorFilter.regime,
        appliedConfiguratorFilter.candleDirection,
        appliedConfiguratorFilter.candleWindow,
        appliedConfiguratorFilter.tradeDirection,
        appliedConfiguratorFilter.session
      );
    }
    return slices;
  }, [appliedConfiguratorFilter, drilldownFilterEffective, slices]);

  useEffect(() => {
    if (!slices.length) return;
    if (slices.some((s) => s.id === selectedId)) return;
    setSelectedId(slices.find((s) => s.kind === "all")?.id ?? slices[0]?.id ?? "all");
  }, [slices, selectedId]);

  const showUniformSegmentContext = !isTradeAnalysisReportType(props.reportType);

  const handleTableSliceSelect = useCallback((slice: ConditionAnalysisSlice) => {
    setSelectedId(slice.id);
    setConfiguredSliceId(null);

    if (slice.kind === "all") {
      setConfigRegime("");
      setConfigCandleDirection("");
      setConfigCandleWindow("");
      setConfigTradeDirection("");
      setConfigSession("");
      return;
    }

    const ctx = reportSliceContextFromSelected(slice);
    if (!ctx) return;
    const fields = sliceContextToConfiguratorFields(ctx);
    setConfigRegime(fields.configRegime);
    setConfigCandleDirection(fields.configCandleDirection);
    setConfigCandleWindow(fields.configCandleWindow);
    setConfigTradeDirection(fields.configTradeDirection);
    setConfigSession(fields.configSession);
  }, []);

  const scheduleScrollMarketContextIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        marketContextSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, []);

  const handleOpenTableSlice = useCallback(
    (slice: ConditionAnalysisSlice) => {
      if (slice.kind === "all") return;

      const ctx = reportSliceContextFromSelected(slice);
      if (ctx) {
        const fields = sliceContextToConfiguratorFields(ctx);
        setConfigRegime(fields.configRegime);
        setConfigCandleDirection(fields.configCandleDirection);
        setConfigCandleWindow(fields.configCandleWindow);
        setConfigTradeDirection(fields.configTradeDirection);
        setConfigSession(fields.configSession);
      }

      setConfiguredSliceId(slice.id);
      setSelectedId(slice.id);
      toast.success("Opened exact slice", {
        description: `Now showing “${getDisplaySliceName(slice, props.candleTimeframe)}” only. Matching-slices browse is paused while this slice is open.`,
      });
      scheduleScrollMarketContextIntoView();
    },
    [scheduleScrollMarketContextIntoView]
  );

  const clearConfiguratorState = useCallback(() => {
    setConfiguredSliceId(null);
    setAppliedConfiguratorFilter(null);
    setConfigRegime("");
    setConfigCandleWindow("");
    setConfigCandleDirection("");
    setConfigTradeDirection("");
    setConfigSession("");
    setSelectedId("all");
    props.onDismissMarketContextFocus?.();
  }, [props.onDismissMarketContextFocus]);

  const applyDrilldownFilter = useCallback(
    (overrides?: Partial<AppliedConfiguratorFilter>, options?: { successDescription?: string }) => {
      const filter: AppliedConfiguratorFilter = {
        regime: overrides?.regime ?? configRegime,
        candleDirection: overrides?.candleDirection ?? configCandleDirection,
        candleWindow: overrides?.candleWindow ?? configCandleWindow,
        tradeDirection: overrides?.tradeDirection ?? configTradeDirection,
        session: overrides?.session ?? configSession,
      };
      if (configuratorCriteriaCountFromFields(filter.regime, filter.candleDirection, filter.tradeDirection, filter.session) === 0) {
        setAppliedConfiguratorFilter(null);
        return;
      }
      setConfigRegime(filter.regime);
      setConfigCandleDirection(filter.candleDirection);
      setConfigCandleWindow(filter.candleWindow);
      setConfigTradeDirection(filter.tradeDirection);
      setConfigSession(filter.session);
      setAppliedConfiguratorFilter(filter);
      setConfiguredSliceId(null);
      const matches = findMatchingConfiguratorSlices(
        slices,
        filter.regime,
        filter.candleDirection,
        filter.candleWindow,
        filter.tradeDirection,
        filter.session
      ).filter((s) => s.kind !== "all");
      // One exact match → highlight it so buckets/situations align with that row.
      // Multiple matches → keep All trades selected; buckets stay All-trades until a row is clicked or Open.
      setSelectedId(matches.length === 1 ? matches[0]!.id : "all");
      const summary = formatConfiguratorFilterSummary(filter, props.candleTimeframe);
      toast.success("Matching slices shown", {
        description:
          options?.successDescription ??
          (summary
            ? matches.length === 1
              ? `Showing the one slice that includes ${summary}. Buckets below are for that slice — use Open for the locked exact-slice view.`
              : `Exploration table lists slices that include ${summary}. Buckets stay on All trades until you select a row or use Open.`
            : "Exploration table is narrowed to your selected dimensions."),
      });
      scheduleScrollMarketContextIntoView();
    },
    [
      configCandleDirection,
      configCandleWindow,
      configRegime,
      configSession,
      configTradeDirection,
      scheduleScrollMarketContextIntoView,
      slices,
    ]
  );

  const applyFocusSlice = useCallback(
    (
      regime: string,
      candleDirection: "" | MarketContextCandleDirection,
      candleWindow: "" | "long" | "short",
      tradeDirection: "" | "long" | "short",
      session: string,
      options?: { successDescription?: string }
    ) => {
      if (configuratorCriteriaCountFromFields(regime, candleDirection, tradeDirection, session) === 0) {
        toast.info("Choose at least one dimension", {
          description: "Select regime, market direction, trade direction, and/or session, then click Open this slice.",
        });
        return;
      }

      const best = findBestMatchingConfiguratorSlice(
        slices,
        regime,
        candleDirection,
        candleWindow,
        tradeDirection,
        session
      );

      if (!best) {
        toast.info("No matching slice", {
          description: "No computed slice matches this exact combination in the current report snapshot.",
        });
        return;
      }

      setConfiguredSliceId(best.id);
      if (isSingleSliceKind(best.kind)) {
        setSelectedId(best.id);
      }

      toast.success("Opened exact slice", {
        description:
          options?.successDescription ??
          `Now showing “${getDisplaySliceName(best, props.candleTimeframe)}” only. Matching-slices browse is paused while this slice is open.`,
      });
      scheduleScrollMarketContextIntoView();
    },
    [scheduleScrollMarketContextIntoView, slices]
  );

  const applyDrilldownFilterFromConfigurator = useCallback(() => {
    applyDrilldownFilter();
  }, [applyDrilldownFilter]);

  const applyFocusSliceFromConfigurator = useCallback(() => {
    applyFocusSlice(
      configRegime,
      configCandleDirection,
      configCandleWindow,
      configTradeDirection,
      configSession
    );
  }, [
    applyFocusSlice,
    configCandleDirection,
    configCandleWindow,
    configRegime,
    configSession,
    configTradeDirection,
  ]);

  useEffect(() => {
    const target = props.marketContextFocus;
    const nonce = props.marketContextFocusNonce ?? 0;
    if (!target || !nonce) return;

    if (target.marketBucketsTab || target.highlightSituation || target.highlightBucket) {
      if (lastAppliedMarketContextFocusNonceRef.current === nonce) return;
      lastAppliedMarketContextFocusNonceRef.current = nonce;
      setConfiguredSliceId(null);
      setAppliedConfiguratorFilter(null);
      setConfigRegime("");
      setConfigCandleDirection("");
      setConfigCandleWindow("");
      setConfigTradeDirection("");
      setConfigSession("");
      setSelectedId("all");
      const tabLabel =
        target.marketBucketsTab === "best-situations"
          ? "Best situations"
          : target.marketBucketsTab === "worst-situations"
            ? "Worst situations"
            : target.marketBucketsTab === "worst-buckets"
              ? "Worst buckets"
              : target.marketBucketsTab === "best-buckets"
                ? "Best buckets"
                : "Market";
      const rowLabel = target.highlightSituation ?? target.highlightBucket;
      toast.success("Opened in Market", {
        description: `${tabLabel} · All trades${rowLabel ? ` · ${rowLabel.replace(/\|/g, " | ")}` : ""}`,
      });
      // Row scroll is handled in MarketContextBucketsSituationsTabs (after the sub-tab mounts).
      return;
    }

    const preferred =
      target.preferredSliceId && slices.some((s) => s.id === target.preferredSliceId)
        ? slices.find((s) => s.id === target.preferredSliceId) ?? null
        : null;

    if (target.preferredSliceId && !preferred) {
      setConfigRegime(target.configRegime);
      setConfigCandleDirection(target.configCandleDirection);
      setConfigCandleWindow(target.configCandleWindow);
      setConfigTradeDirection(target.configTradeDirection);
      setConfigSession(target.configSession);
      return;
    }

    if (lastAppliedMarketContextFocusNonceRef.current === nonce) return;
    lastAppliedMarketContextFocusNonceRef.current = nonce;

    setConfigRegime(target.configRegime);
    setConfigCandleDirection(target.configCandleDirection);
    setConfigCandleWindow(target.configCandleWindow);
    setConfigTradeDirection(target.configTradeDirection);
    setConfigSession(target.configSession);

    if (preferred && preferred.kind !== "all") {
      setConfiguredSliceId(preferred.id);
      if (isSingleSliceKind(preferred.kind)) {
        setSelectedId(preferred.id);
      }
      toast.success("Opened exact slice", {
        description: `Opened “${getDisplaySliceName(preferred, props.candleTimeframe)}” from this finding.`,
      });
      scheduleScrollMarketContextIntoView();
      return;
    }

    applyDrilldownFilter(
      {
        regime: target.configRegime,
        candleDirection: target.configCandleDirection,
        candleWindow: target.configCandleWindow,
        tradeDirection: target.configTradeDirection,
        session: target.configSession,
      },
      { successDescription: "All-trades tables now show matching slices from this finding." }
    );
  }, [
    applyDrilldownFilter,
    props.marketContextFocus,
    props.marketContextFocusNonce,
    scheduleScrollMarketContextIntoView,
    slices,
  ]);

  const resolvedManagementLookup = useMemo(() => {
    if (props.managementLookup) return props.managementLookup;
    if (!props.managementCatalog?.length && !props.managementCandidates?.length) return null;
    return buildManagementCatalogLookup(props.managementCatalog, props.managementCandidates);
  }, [props.managementCatalog, props.managementCandidates, props.managementLookup]);

  const configuratorSegmentKey = useMemo(() => {
    if (!configRegime && !configCandleDirection && !configTradeDirection) return null;
    return segmentKeyFromConfigurator(
      configRegime,
      configCandleDirection,
      configTradeDirection,
      configCandleWindow === "short" ? "short" : "long"
    );
  }, [configCandleDirection, configCandleWindow, configRegime, configTradeDirection]);

  const headlineSegmentKey = useMemo(() => {
    if (configuratorSegmentKey) return configuratorSegmentKey;
    const slice = segmentHeadlineSlice;
    if (!slice) return null;
    const regime = String(slice.market_condition ?? "").trim();
    const direction = String(slice.candle_direction ?? "").trim();
    const tradeDir = String(slice.direction ?? "").trim();
    if (!regime || !direction || !tradeDir) return null;
    const window = getSliceCandleWindow(slice) === "short" ? "short" : "long";
    return segmentKeyFromConfigurator(regime, direction, tradeDir, window);
  }, [configuratorSegmentKey, segmentHeadlineSlice]);

  const activeManagementEntry = useMemo(() => {
    if (!headlineSegmentKey || !resolvedManagementLookup) return null;
    return resolvedManagementLookup.get(headlineSegmentKey) ?? null;
  }, [headlineSegmentKey, resolvedManagementLookup]);

  const managementStrip = useMemo(() => {
    if (!activeManagementEntry?.entry || !props.onPreviewManagement) return null;
    const segmentKey = headlineSegmentKey ?? activeManagementEntry.entry.segment_key ?? "";
    return (
      <ManagementPolicyInlineStrip
        entry={activeManagementEntry.entry}
        isTesCandidate={activeManagementEntry.isTesCandidate}
        segmentKey={segmentKey}
        previewLoading={props.managementPreviewLoadingKey === segmentKey}
        onPreview={() => {
          void props.onPreviewManagement?.({
            segmentKey,
            segmentScope:
              activeManagementEntry.entry.segment_scope ??
              segmentScopeFromSegmentKey(segmentKey),
          });
        }}
        onEditInSimulator={
          props.onOpenManagementInSimulator
            ? () => {
                const policy =
                  activeManagementEntry.entry.simulation_policy ??
                  activeManagementEntry.candidate?.simulation_policy;
                if (policy) {
                  props.onOpenManagementInSimulator?.({ segmentKey, policy });
                }
              }
            : undefined
        }
      />
    );
  }, [
    activeManagementEntry,
    headlineSegmentKey,
    props.managementPreviewLoadingKey,
    props.onOpenManagementInSimulator,
    props.onPreviewManagement,
  ]);

  const onPreviewSituation = useMemo(() => {
    if (!props.onPreviewManagement || !headlineSegmentKey) return undefined;
    return (situation: string) => {
      void props.onPreviewManagement?.({
        segmentKey: headlineSegmentKey,
        segmentScope: segmentScopeFromSegmentKey(headlineSegmentKey),
        situation,
      });
    };
  }, [headlineSegmentKey, props.onPreviewManagement]);

  if (slices.length === 0 || !selectedNode) {
    return (
      <Card {...reportDetailSectionCardProps}>
        <CardHeader pb={0}>
          <Box>
            <Text fontSize="lg" fontWeight="semibold" color={props.textColor}>
              Entry Analysis
            </Text>
            <Text fontSize="sm" color={props.subtleText} mt={1}>
              No condition analysis found in this report snapshot.
            </Text>
          </Box>
        </CardHeader>
      </Card>
    );
  }

  const regimeActionableFilters = useMemo(
    () => getRegimeActionableFilters(conditionAnalysisSource),
    [conditionAnalysisSource]
  );

  const pivotEvidenceFocusNonce =
    props.marketContextFocusNonce &&
    props.marketContextFocus &&
    !props.marketContextFocus.marketBucketsTab &&
    !props.marketContextFocus.highlightSituation
      ? props.marketContextFocusNonce
      : undefined;

  const bestBuckets = selectedNode.best_conditions ?? [];
  const worstBuckets = selectedNode.worst_conditions ?? [];
  const bestSituations = useMemo(() => {
    const fromNode = selectedNode.market_condition_brackets?.best_situations ?? [];
    if (selectedId === "all" && !isFocusedSegmentView) {
      const fromFilters = regimeActionableFilters?.best_situations;
      if (Array.isArray(fromFilters) && fromFilters.length > 0) {
        return enrichSituationRowsWithPolicyNodes(
          fromFilters as ConditionAnalysisSituationRow[],
          fromNode
        );
      }
    }
    return fromNode;
  }, [
    isFocusedSegmentView,
    regimeActionableFilters?.best_situations,
    selectedId,
    selectedNode.market_condition_brackets?.best_situations,
  ]);
  const worstSituations = useMemo(() => {
    const fromNode = selectedNode.market_condition_brackets?.worst_situations ?? [];
    if (selectedId === "all" && !isFocusedSegmentView) {
      const fromFilters = regimeActionableFilters?.worst_situations;
      if (Array.isArray(fromFilters) && fromFilters.length > 0) {
        return enrichSituationRowsWithPolicyNodes(
          fromFilters as ConditionAnalysisSituationRow[],
          fromNode
        );
      }
    }
    return fromNode;
  }, [
    isFocusedSegmentView,
    regimeActionableFilters?.worst_situations,
    selectedId,
    selectedNode.market_condition_brackets?.worst_situations,
  ]);

  const pickedBucketKeys = useMemo(() => {
    const s = new Set<string>();
    for (const p of savedPicks) {
      if (p.kind !== "bucket") continue;
      const sliceId = (typeof p.sliceId === "string" ? p.sliceId : "") || String((p.meta as any)?.slice_id ?? "all");
      const bucket = typeof (p.meta as any)?.bucket === "string" ? String((p.meta as any).bucket) : p.label;
      const metricKey = typeof (p.meta as any)?.metric_key === "string" ? String((p.meta as any).metric_key) : "";
      s.add(`${sliceId}::${bucket}::${metricKey}`);
    }
    return s;
  }, [savedPicks]);

  const pickedSituationKeys = useMemo(() => {
    const sig = (clauses: any): string => {
      if (!Array.isArray(clauses)) return "";
      return clauses
        .map((c) => `${String(c?.key ?? "")}=${String(c?.value ?? "")}`)
        .filter((x) => x !== "=")
        .sort()
        .join("|");
    };
    const s = new Set<string>();
    for (const p of savedPicks) {
      if (p.kind !== "situation") continue;
      const sliceId = (typeof p.sliceId === "string" ? p.sliceId : "") || String((p.meta as any)?.slice_id ?? "all");
      const situation = typeof (p.meta as any)?.situation === "string" ? String((p.meta as any).situation) : p.label;
      const clauses = sig((p.meta as any)?.clauses);
      s.add(`${sliceId}::${situation}::${clauses}`);
    }
    return s;
  }, [savedPicks]);

  const pickedSegmentSliceContextIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of savedPicks) {
      if (p.kind !== "segment") continue;
      const id = p.sliceContext?.sliceId;
      if (typeof id === "string" && id) s.add(id);
    }
    return s;
  }, [savedPicks]);

  const sliceContextForPreview = useMemo((): ReportPickSliceContext | null => {
    if (!selected || selected.kind === "all") {
      return null;
    }
    return reportSliceContextFromSelected(selected) ?? null;
  }, [selected]);

  const analysisScopeForPreview = selectedNode?.analysis_scope ?? null;

  const cohortSlicePathLabel = useMemo(
    () => getCohortPreviewSlicePathLabel(selected),
    [selected],
  );

  const onPreviewBucketTrades = useCallback(
    (row: ConditionAnalysisBucketRow) => {
      const label = buildCohortPreviewDisplayLabel(cohortSlicePathLabel, String(row.bucket ?? "Bucket"));
      props.onPreviewConditionBucket?.(row, sliceContextForPreview, analysisScopeForPreview, label);
    },
    [analysisScopeForPreview, cohortSlicePathLabel, props.onPreviewConditionBucket, sliceContextForPreview]
  );

  const onPreviewSituationTrades = useCallback(
    (row: ConditionAnalysisSituationRow) => {
      const rowLabel = String(row.situation ?? "Situation").replace(/\s*\|\s*/g, " + ");
      const label = buildCohortPreviewDisplayLabel(cohortSlicePathLabel, rowLabel);
      props.onPreviewConditionSituation?.(row, sliceContextForPreview, analysisScopeForPreview, label);
    },
    [analysisScopeForPreview, cohortSlicePathLabel, props.onPreviewConditionSituation, sliceContextForPreview]
  );

  const onPreviewSliceTrades = useCallback(() => {
    if (!segmentHeadlineSlice || !props.onPreviewConditionSlice) return;
    const headline = sliceHeadline(segmentHeadlineSlice, { sessionMarginalSourceNode: allTradesNode });
    const label =
      getCohortPreviewSlicePathLabel(segmentHeadlineSlice) ?? getDisplaySliceName(segmentHeadlineSlice, props.candleTimeframe);
    props.onPreviewConditionSlice(
      segmentHeadlineSlice,
      reportSliceContextFromSelected(segmentHeadlineSlice) ?? sliceContextForPreview,
      label,
      headline.sample
    );
  }, [allTradesNode, props.onPreviewConditionSlice, segmentHeadlineSlice, sliceContextForPreview]);

  const onPreviewPivotSliceTrades = useCallback(
    (slice: ConditionAnalysisSlice) => {
      if (!props.onPreviewConditionSlice) return;
      const headline = sliceHeadline(slice, { sessionMarginalSourceNode: allTradesNode });
      const label = getCohortPreviewSlicePathLabel(slice) ?? getDisplaySliceName(slice, props.candleTimeframe);
      props.onPreviewConditionSlice(
        slice,
        reportSliceContextFromSelected(slice) ?? null,
        label,
        headline.sample
      );
    },
    [allTradesNode, props.onPreviewConditionSlice, sliceHeadline]
  );

  const commitPick = useCallback((pick: ReportPick) => {
    const res = addPick({ ...pick, simulatorLane: "single" });
    if (res.ok) {
      toast.success("Added to picks", {
        description: `${pick.label} · Available Single Picks`,
      });
    } else {
      toast.info("Pick already added", { description: (res as { reason?: string }).reason });
    }
  }, []);

  const onPickBucket = useMemo(() => {
    return (row: ConditionAnalysisBucketRow, metricKey: string) => {
      const sliceCtx = reportSliceContextFromSelected(selected);
      const suggestions =
        (row as any).policy_import_nodes ??
        (row as any).simulator_suggestions ??
        (row as any).simulator_suggestion_nodes ??
        (row as any).simulator_suggestion_node_for_metric_bucket ??
        (row as any).simulator_suggestion_node ??
        null;
      const meta = {
        bucket: row.bucket,
        metric_key: metricKey,
        slice_id: selected?.id ?? "all",
        ...(sliceCtx ? { slice_context: sliceCtx } : {}),
        ...quickStatsMetaPatch(pickQuickStatsFromMetrics(row)),
      };
      const pick: ReportPick = {
        v: 1,
        id: buildPickId("bucket", row.bucket, meta, props.reportId),
        createdAtMs: Date.now(),
        kind: "bucket",
        label: row.bucket,
        ...(props.reportId ? { reportId: props.reportId } : {}),
        sliceId: selected?.id ?? "all",
        ...(sliceCtx ? { sliceContext: sliceCtx } : {}),
        ...(Array.isArray(suggestions) && suggestions.length
          ? { policyNodes: suggestions }
          : suggestions && typeof suggestions === "object"
            ? { policyNodes: [suggestions] }
            : {}),
        meta,
      };
      commitPick(pick);
    };
  }, [commitPick, props.reportId, selected]);

  const onPickTableSlice = useCallback(
    (slice: ConditionAnalysisSlice) => {
      const sliceCtx = reportSliceContextFromSelected(slice);
      if (!sliceCtx) {
        toastWarning("Cannot add pick", { description: "This slice is not supported for simulator picks." });
        return;
      }
      const node = slice.node as Record<string, unknown> | null;
      const suggestions =
        (node as { policy_import_nodes?: unknown })?.policy_import_nodes ??
        (node as { simulator_suggestions?: unknown })?.simulator_suggestions ??
        (node as { simulator_suggestion_nodes?: unknown })?.simulator_suggestion_nodes ??
        (node as { simulator_suggestion_node?: unknown })?.simulator_suggestion_node ??
        null;
      const headline = sliceHeadline(slice, { sessionMarginalSourceNode: allTradesNode });
      const meta: Record<string, unknown> = {
        source: "configured_slice_result",
        view_slice_id: selected?.id ?? "all",
        ...quickStatsMetaPatch(
          pickQuickStatsFromMetrics({
            sample_size: headline.sample,
            total_pnl: headline.totalPnl,
            win_rate: headline.winRate,
            expectancy: headline.expectancy,
          })
        ),
      };
      const pick: ReportPick = {
        v: 1,
        id: buildPickId("segment", sliceCtx.label, meta, props.reportId),
        createdAtMs: Date.now(),
        kind: "segment",
        label: sliceCtx.label,
        ...(props.reportId ? { reportId: props.reportId } : {}),
        sliceId: sliceCtx.sliceId,
        sliceContext: sliceCtx,
        meta,
        ...(Array.isArray(suggestions) && suggestions.length
          ? { policyNodes: suggestions }
          : suggestions && typeof suggestions === "object"
            ? { policyNodes: [suggestions] }
            : {}),
      };
      commitPick(pick);
    },
    [allTradesNode, commitPick, props.reportId, selected?.id]
  );

  const onPickConfiguredSliceResult = useCallback(() => {
    if (!segmentHeadlineSlice) return;
    onPickTableSlice(segmentHeadlineSlice);
  }, [onPickTableSlice, segmentHeadlineSlice]);

  const onPickSituation = useMemo(() => {
    return (row: ConditionAnalysisSituationRow) => {
      const label = row.situation ?? "Situation";
      const sliceCtx = reportSliceContextFromSelected(selected);
      const suggestions =
        (row as any).policy_import_nodes ??
        (row as any).simulator_suggestions ??
        (row as any).simulator_suggestion_nodes ??
        (row as any).simulator_suggestion_node_for_situation ??
        (row as any).simulator_suggestion_node ??
        null;
      const meta = {
        situation: row.situation ?? null,
        clauses: row.clauses ?? [],
        slice_id: selected?.id ?? "all",
        ...(sliceCtx ? { slice_context: sliceCtx } : {}),
        ...quickStatsMetaPatch(pickQuickStatsFromMetrics(row)),
      };
      const pick: ReportPick = {
        v: 1,
        id: buildPickId("situation", label, meta, props.reportId),
        createdAtMs: Date.now(),
        kind: "situation",
        label,
        ...(props.reportId ? { reportId: props.reportId } : {}),
        sliceId: selected?.id ?? "all",
        ...(sliceCtx ? { sliceContext: sliceCtx } : {}),
        ...(Array.isArray(suggestions) && suggestions.length
          ? { policyNodes: suggestions }
          : suggestions && typeof suggestions === "object"
            ? { policyNodes: [suggestions] }
            : {}),
        meta,
      };
      commitPick(pick);
    };
  }, [commitPick, props.reportId, selected]);

  return (
    <SimulatorPickLockProvider
      disabled={Boolean(props.simulatorPicksDisabled)}
      reason={props.simulatorPicksDisabledReason}
    >
    <Box
      ref={marketContextSectionRef}
      w="100%"
      scrollMarginTop={{ base: "4.5rem", md: "5.5rem" }}
    >
      <Card {...reportDetailSectionCardProps} data-market-context-section-card="">
        <CardHeader pb={2}>
          <Box flex="1" minW="260px">
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              color="#38BBD1"
              fontWeight="normal"
              fontFamily="'Urbanist', sans-serif"
              lineHeight="1.15"
            >
              Entry Analysis
            </Text>
            <Text fontSize="sm" color={props.subtleText} mt={1}>
              Explore how your results change across market environments at trade entry. You will see performance
              breakdowns by regime, market candle direction (bar windows scale with the report candle timeframe), and trade direction, including combined
              slices that reveal where
              your edge is strongest or weakest. Use the Slice Configurator to filter All-trades drilldowns or focus a
              single segment; click a pivot row to focus that slice immediately.
            </Text>
          </Box>
        </CardHeader>
      <CardBody pt={0} overflowX="visible" overflowY="visible">
        <Stack spacing={4}>
          <Box
            as="div"
            data-market-context-slices-card=""
            mt={3}
            overflow="visible"
            minW={0}
          >
            <Stack spacing={4}>
            {/*
              Layout is inline on purpose: workspace report sheets are often ~550–900px while
              viewport breakpoints still fire at xl, and @layer components CSS loses fights with
              utilities. Inline layout cannot be overridden except by !important.
            */}
            <div
              data-slice-configurator-shell=""
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 12,
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                <Text
                  fontSize="sm"
                  color="#38BBD1"
                  fontWeight="medium"
                  fontFamily="'Urbanist', sans-serif"
                  textTransform="none"
                  letterSpacing="normal"
                >
                  Slice configurator
                </Text>
                <TooltipProvider delayDuration={300}>
                  <ShadcnTooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex size-[18px] shrink-0 cursor-help items-center justify-center text-gray-500 dark:text-sidebar-muted-foreground [&_svg]:text-gray-500 dark:[&_svg]:text-sidebar-muted-foreground"
                        aria-label="About slice configurator"
                      >
                        <CircleHelpIcon
                          size={16}
                          className="!text-gray-500 dark:!text-sidebar-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      sideOffset={4}
                      arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
                      className={metricTooltipShellClassName("max-w-[300px]")}
                    >
                      <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
                        {SLICE_CONFIGURATOR_TOOLTIP}
                      </p>
                    </TooltipContent>
                  </ShadcnTooltip>
                </TooltipProvider>
                </div>
              </div>

              {/* Selects + actions share one wrap row: buttons sit with the last select when
                  space allows, otherwise drop below the select rows. */}
              <div
                data-slice-configurator-filters=""
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                  gap: 12,
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <div
                  data-slice-configurator-field=""
                  style={{ flex: "1 1 14rem", minWidth: "min(100%, 14rem)", maxWidth: "100%" }}
                >
                  <TradeFilterSelect
                    layout="full"
                    value={configRegime}
                    placeholder={sliceConfiguratorAnyRegimeLabel}
                    onValueChange={setConfigRegime}
                    options={[
                      { value: "", label: sliceConfiguratorAnyRegimeLabel },
                      ...regimeConfiguratorOptions,
                    ]}
                  />
                </div>
                <div
                  data-slice-configurator-field=""
                  style={{ flex: "1 1 14rem", minWidth: "min(100%, 14rem)", maxWidth: "100%" }}
                >
                  <TradeFilterSelect
                    layout="full"
                    value={
                      configCandleDirection && configCandleWindow
                        ? `${configCandleWindow}:${configCandleDirection}`
                        : ""
                    }
                    placeholder={sliceConfiguratorAnyCandleDirectionLabel}
                    onValueChange={(value) => {
                      if (!value) {
                        setConfigCandleDirection("");
                        setConfigCandleWindow("");
                        return;
                      }
                      const [windowValue, directionValue] = value.split(":");
                      setConfigCandleWindow((windowValue as "" | "long" | "short") ?? "");
                      setConfigCandleDirection((directionValue as "" | MarketContextCandleDirection) ?? "");
                    }}
                    options={[
                      { value: "", label: sliceConfiguratorAnyCandleDirectionLabel },
                      {
                        value: "short:up",
                        label: `Up (${directionShortBarsLabel}) (${fmtInt(candleShortMarginalCounts.get("up") ?? 0)})`,
                        disabled: !availableCandleDirectionSet.has("short:up"),
                      },
                      {
                        value: "short:down",
                        label: `Down (${directionShortBarsLabel}) (${fmtInt(candleShortMarginalCounts.get("down") ?? 0)})`,
                        disabled: !availableCandleDirectionSet.has("short:down"),
                      },
                      {
                        value: "short:flat",
                        label: `Flat (${directionShortBarsLabel}) (${fmtInt(candleShortMarginalCounts.get("flat") ?? 0)})`,
                        disabled: !availableCandleDirectionSet.has("short:flat"),
                      },
                      {
                        value: "long:up",
                        label: `Up (${directionLongBarsLabel}) (${fmtInt(candleLongMarginalCounts.get("up") ?? 0)})`,
                        disabled: !availableCandleDirectionSet.has("long:up"),
                      },
                      {
                        value: "long:down",
                        label: `Down (${directionLongBarsLabel}) (${fmtInt(candleLongMarginalCounts.get("down") ?? 0)})`,
                        disabled: !availableCandleDirectionSet.has("long:down"),
                      },
                      {
                        value: "long:flat",
                        label: `Flat (${directionLongBarsLabel}) (${fmtInt(candleLongMarginalCounts.get("flat") ?? 0)})`,
                        disabled: !availableCandleDirectionSet.has("long:flat"),
                      },
                    ]}
                  />
                </div>
                <div
                  data-slice-configurator-field=""
                  style={{ flex: "1 1 14rem", minWidth: "min(100%, 14rem)", maxWidth: "100%" }}
                >
                  <TradeFilterSelect
                    layout="full"
                    value={configTradeDirection}
                    placeholder={sliceConfiguratorAnyTradeDirectionLabel}
                    onValueChange={(value) =>
                      setConfigTradeDirection((value as "" | "long" | "short") ?? "")
                    }
                    options={[
                      { value: "", label: sliceConfiguratorAnyTradeDirectionLabel },
                      {
                        value: "long",
                        label: `Long (${fmtInt(tradeDirMarginalCounts.get("long") ?? 0)})`,
                        disabled: !availableTradeDirectionSet.has("long"),
                      },
                      {
                        value: "short",
                        label: `Short (${fmtInt(tradeDirMarginalCounts.get("short") ?? 0)})`,
                        disabled: !availableTradeDirectionSet.has("short"),
                      },
                    ]}
                  />
                </div>
                <div
                  data-slice-configurator-field=""
                  style={{ flex: "1 1 14rem", minWidth: "min(100%, 14rem)", maxWidth: "100%" }}
                >
                  <TradeFilterSelect
                    layout="full"
                    value={configSession}
                    placeholder={sliceConfiguratorAnySessionLabel}
                    onValueChange={(value) => setConfigSession(value)}
                    options={[
                      { value: "", label: sliceConfiguratorAnySessionLabel },
                      ...sessionConfiguratorOptions.map((r) => ({
                        value: r.key,
                        label: `${r.key.replace(/_/g, " ")} (${fmtInt(r.sample_size)})`,
                      })),
                    ]}
                  />
                </div>

                <div
                  data-slice-configurator-actions=""
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 8,
                    flex: "0 0 auto",
                    minWidth: "min-content",
                    maxWidth: "100%",
                    marginLeft: "auto",
                  }}
                >
                  <ShadcnButton
                    type="button"
                    variant="outline"
                    size="sm"
                    data-font="primary"
                    className={cn(
                      "h-9 shrink-0 font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal",
                      !isConfiguratorActive && "invisible pointer-events-none",
                    )}
                    style={{ height: "2.25rem", minHeight: "2.25rem", maxHeight: "2.25rem" }}
                    onClick={clearConfiguratorState}
                  >
                    Clear
                  </ShadcnButton>
                  <div
                    data-slice-configurator-actions-end=""
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  >
                    <TooltipProvider delayDuration={300}>
                      <ShadcnTooltip>
                        <TooltipTrigger asChild>
                          <ShadcnButton
                            type="button"
                            variant="outline-brand"
                            size="sm"
                            data-font="primary"
                            data-market-context-filter-drilldown=""
                            className="h-9 shrink-0 font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal !border-[color:var(--brand-tropical-surf)] !text-[color:var(--brand-tropical-surf)] !bg-transparent hover:!bg-[color:var(--brand-tropical-surf)]/10 hover:!text-[color:var(--brand-tropical-surf)]"
                            style={{
                              height: "2.25rem",
                              minHeight: "2.25rem",
                              maxHeight: "2.25rem",
                              color: "#38BBD1",
                              borderColor: "#38BBD1",
                              borderWidth: 1,
                              borderStyle: "solid",
                              backgroundColor: "transparent",
                            }}
                            onClick={applyDrilldownFilterFromConfigurator}
                          >
                            Show matching slices
                          </ShadcnButton>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          sideOffset={4}
                          arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
                          className={metricTooltipShellClassName("max-w-[280px]")}
                        >
                          <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
                            {SHOW_MATCHING_SLICES_TOOLTIP}
                          </p>
                        </TooltipContent>
                      </ShadcnTooltip>
                    </TooltipProvider>
                    <TooltipProvider delayDuration={300}>
                      <ShadcnTooltip>
                        <TooltipTrigger asChild>
                          <ShadcnButton
                            type="button"
                            variant="gradient"
                            size="sm"
                            data-font="primary"
                            data-market-context-apply=""
                            className="h-9 shrink-0 font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal active:translate-y-0"
                            style={{ height: "2.25rem", minHeight: "2.25rem", maxHeight: "2.25rem" }}
                            onClick={applyFocusSliceFromConfigurator}
                          >
                            Open this slice
                          </ShadcnButton>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          sideOffset={4}
                          arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
                          className={metricTooltipShellClassName("max-w-[280px]")}
                        >
                          <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
                            {OPEN_THIS_SLICE_TOOLTIP}
                          </p>
                        </TooltipContent>
                      </ShadcnTooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </div>

            {!isConfiguredCombinedSlice ? (
              <MarketContextPivotTable
                embedded
                candleTimeframe={props.candleTimeframe}
                title={
                  drilldownFilterEffective && appliedConfiguratorFilter
                    ? `Matching slices: ${formatConfiguratorFilterSummary(appliedConfiguratorFilter, props.candleTimeframe)}`
                    : "Slice exploration"
                }
                description={
                  drilldownFilterEffective
                    ? "Slices that include every dimension you selected in the configurator. Long lists start collapsed to the top 10 — expand or drag the edge to see more."
                    : "All slice categories in one table. Long lists start collapsed to the top 10 — expand or drag the edge to see more. Sort any column to reorder."
                }
                slices={mainTableSlices}
                selectedId={selectedTableSlice?.id ?? "all"}
                onSelect={handleTableSliceSelect}
                sliceHeadline={sliceHeadline}
                metricDefinitions={mergedMetricDefinitions}
                sessionMarginalSourceNode={allTradesNode}
                evidenceFocusNonce={pivotEvidenceFocusNonce}
                onPreviewSlice={props.onPreviewConditionSlice ? onPreviewPivotSliceTrades : undefined}
                previewActiveKey={props.previewActiveKey}
                previewLoadingKey={props.previewLoadingKey}
                onPickSlice={onPickTableSlice}
                isSlicePicked={(slice) => pickedSegmentSliceContextIds.has(slice.id)}
                onOpenSlice={handleOpenTableSlice}
                hideSliceCollapseControls={props.hideSliceTableCollapseControls}
              />
            ) : null}
            </Stack>
          </Box>

          <SimpleGrid columns={{ base: 1, lg: 1 }} spacing={4}>
            <Box minW={0}>
              {isConfiguredCombinedSlice ? (
                configuredSliceHasNoResults ? (
                  <Alert status="info" borderRadius="6px">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>No results found</AlertTitle>
                      <AlertDescription>
                        This configured slice has no trades in the current report snapshot. Try adjusting one or more
                        dimensions, then click Open this slice again.
                      </AlertDescription>
                    </Box>
                  </Alert>
                ) : segmentHeadlineSlice ? (
                  <MarketContextConfiguredSliceResultTable
                    title="Exact slice result"
                    description="Metrics for the one combination that matches your selects exactly."
                    row={{
                      id: segmentHeadlineSlice.id,
                      sliceLabel: getDisplaySliceName(segmentHeadlineSlice, props.candleTimeframe),
                      ...sliceHeadline(segmentHeadlineSlice, {
                        sessionMarginalSourceNode: allTradesNode,
                      }),
                    }}
                    metricDefinitions={mergedMetricDefinitions}
                    onAddToSimulator={onPickConfiguredSliceResult}
                    onPreviewSlice={
                      props.onPreviewConditionSlice && segmentHeadlineMetrics?.sample !== 0
                        ? onPreviewSliceTrades
                        : undefined
                    }
                    previewActiveKey={props.previewActiveKey}
                    previewLoadingKey={props.previewLoadingKey}
                    previewDisabled={
                      typeof segmentHeadlineMetrics?.sample === "number"
                        ? segmentHeadlineMetrics.sample <= 0
                        : false
                    }
                    isSlicePicked={pickedSegmentSliceContextIds.has(segmentHeadlineSlice.id)}
                    managementStrip={managementStrip}
                  />
                ) : null
              ) : null}

              {showRegimeSummaryContextNotes && allTradesNode ? (
                <RegimeSummaryContextNotesPanel
                  node={allTradesNode}
                  showUniformSegmentContext={showUniformSegmentContext}
                  borderColor={borderColor}
                  panelBg={panelBg}
                  emptyPanelBg={emptyPanelBg}
                  subtleText={props.subtleText}
                  textColor={props.textColor}
                />
              ) : null}

              {(selectedTableSlice?.kind === "all" || segmentHeadlineSlice != null) &&
              !(isConfiguredCombinedSlice && configuredSliceHasNoResults) ? (
                <Box mt={showRegimeSummaryContextNotes ? 4 : 2}>
                  <MarketContextBucketsSituationsTabs
                    mode={isFocusedSegmentView ? "segment" : "full"}
                    bestBuckets={bestBuckets}
                    worstBuckets={worstBuckets}
                    bestSituations={bestSituations}
                    worstSituations={worstSituations}
                    metricDefinitions={mergedMetricDefinitions}
                    onPickBucket={onPickBucket}
                    onPickSituation={onPickSituation}
                    onPreviewBucket={props.onPreviewConditionBucket ? onPreviewBucketTrades : undefined}
                    onPreviewSituationRow={
                      props.onPreviewConditionSituation ? onPreviewSituationTrades : undefined
                    }
                    previewActiveKey={props.previewActiveKey}
                    previewLoadingKey={props.previewLoadingKey}
                    isBucketPicked={(row, metricKey) =>
                      pickedBucketKeys.has(`${selected?.id ?? "all"}::${row.bucket}::${metricKey}`)
                    }
                    isSituationPicked={(row) =>
                      pickedSituationKeys.has(
                        `${selected?.id ?? "all"}::${row.situation ?? "Situation"}::${situationPickSignature(row.clauses)}`,
                      )
                    }
                    showManagementPreview={Boolean(onPreviewSituation)}
                    onPreviewSituation={onPreviewSituation}
                    focusBucketsTab={props.marketContextFocus?.marketBucketsTab ?? null}
                    focusBucketsTabNonce={props.marketContextFocusNonce}
                    highlightSituation={props.marketContextFocus?.highlightSituation ?? null}
                    highlightBucket={props.marketContextFocus?.highlightBucket ?? null}
                    onDismissFocus={props.onDismissMarketContextFocus}
                  />
                </Box>
              ) : null}
            </Box>
          </SimpleGrid>
        </Stack>
      </CardBody>
      <MarketContextDefinitionsModal
        isOpen={isMetricDefinitionsOpen}
        onClose={onMetricDefinitionsClose}
        rows={metricDefinitionRows}
        selectedMetric={selectedMetric}
        definitionsRef={metricDefinitionsRef}
        textColor={props.textColor}
        subtleText={props.subtleText}
        borderColor={borderColor}
        panelBg={panelBg}
      />
      </Card>
    </Box>
    </SimulatorPickLockProvider>
  );
}

